import {useCallback, useEffect, useRef, useState } from "react";
import { Dialog } from "@headlessui/react";
import { Modal, ModalProp } from "../../components/modal/Modal";
import { ModalFoterBox } from "../../components/box/Box";
import Button from "../../components/button/Button";
import { PtAwardedSettings, PtBonusSettings, ptAwardedSettingsTimestampToDate, RangeStepsMap } from "../../model/PointData";
import { Controller, useForm, useWatch } from "react-hook-form";
import { SelectFields, SelectItem } from "../../components/select/SelectFields";
import { RadioButton, RadioButtonItem } from "../../components/radiobutton/RadioButton";
import { useInput } from "../../hooks/useInput";
import { convertDateToSlashTypeZeroPadString } from "../../utility/ConvertUtily";
import TableList, { GridCol, GridRow } from "../../components/table/TableList"
import pointDataQuery from '../../firestore/PointData.query';
import { auth } from "../../firebase";  // ログイン中の団体管理者情報
import { useDispatch } from "react-redux";
import { alertOpen, AlertValue } from "../../features/alert.slice";
import BonusPointSettingsModal, { BonusPointSettings } from "./BonusPointSettingsModal";
import { PointTypeId, BonusSettingsCondition, ConstMessage } from "../../config/const";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { TextField } from "../../components/fields/Fields";
import { generateRandomDigits, isEmptyString } from "../../utility/CommonUtility";
import { convertDateToSlashTypeString } from "../../utility/ConvertUtily";
import { regexHalfNumMinus, validNumber1orMore } from "../../utility/ValidationUtilty";

export type PointAwardedEditProps = {
  // ポイント設定情報
  settings?: PtAwardedSettings;
  // ボーナスポイント設定情報
  bonusSettings?: PtBonusSettings[];
  //ポイント設定詳細のID
  infoDataId?: string;
  //歩数範囲情報
  rangeStepsEditData?: RangeStepsMap[];
}

// バリデーションチェック
const schema = yup.object().shape({
  // ポイントカテゴリ
  // ポイントID
  categoryId: yup.string().required(ConstMessage.SELECTION_REQUIRED),
  // ポイントタイプ
  // タイプID
  typeId: yup.string().when('categoryId', {
    is: (categoryId: string) => (categoryId != undefined && categoryId != ''),
    then: yup.string().required(ConstMessage.SELECTION_REQUIRED)
  }),
  // ポイント名
  name: yup.string().when('typeId', {
    is: (typeId: string) => typeId != '',
    then: yup.string().required()
  }),
  // ポイント数
  _strBasePoint: yup.string().test((value, testContext) => {
    const categoryId: string = testContext.parent.categoryId;
    const typeId: string = testContext.parent.typeId;
    if (categoryId == '' || typeId == '' || typeId == PointTypeId.VITAL_REGIST) return true;

    const pointKind: string = testContext.parent.pointKind;
    if (((typeId == PointTypeId.STEPS_SPECIFIED
      || typeId == PointTypeId.STEPS_TARGET
      || typeId == PointTypeId.STEPS_RANGE)
      && pointKind == '1')
    ) return true;

    return validNumber1orMore(value, testContext, true);
  }),
  // ポイント数（体重登録）
  _strRegistWtPoint: yup.string().test((value, testContext) => {
    const { categoryId, typeId, pointSubject } = testContext.parent;
    if (categoryId === '' || typeId === '' || typeId !== PointTypeId.VITAL_REGIST) {
      return true; // VITAL_REGIST 以外はスキップ
    }
    // 対象が 体重のみ or 両方 → バリデーション実行（必須扱い）
    if (pointSubject === 0 || pointSubject === 2) {
      return validNumber1orMore(value, testContext, true);
    }
    return true;
  }),
  // ポイント数（血圧登録）
  _strRegistBpPoint: yup.string().test((value, testContext) => {
    const { categoryId, typeId, pointSubject } = testContext.parent;
    if (categoryId === '' || typeId === '' || typeId !== PointTypeId.VITAL_REGIST) {
      return true;
    }
    // 対象が 血圧のみ or 両方 → バリデーション実行（必須扱い）
    if (pointSubject === 1 || pointSubject === 2) {
      return validNumber1orMore(value, testContext, true);
    }
    return true;
  }),
  // ポイント数（同時登録）
  _strRegistSamePoint: yup.string().test((value, testContext) => {
    const { categoryId, typeId } = testContext.parent;
    if (categoryId === '' || typeId === '' || typeId !== PointTypeId.VITAL_REGIST) {
      return true;
    }
    // 同時登録は常に任意なので、空でも OK（false渡す）
    return validNumber1orMore(value, testContext, false);
  }),
  // 活動時間
  _strActivityTime: yup.string().test((value, testContext) => {
    const categoryId: string = testContext.parent.categoryId;
    const typeId: string = testContext.parent.typeId;
    if (categoryId == '' || typeId == '' || typeId != PointTypeId.VOLUNTEER_ACTIVITY) return true;

    return validNumber1orMore(value, testContext, false);
  }),
  // ポイント種別
  pointKind: yup.string().test((value, testContext) => {
    const categoryId: string = testContext.parent.categoryId;
    const typeId: string = testContext.parent.typeId;
    if (categoryId == '' || typeId == '' ||
      (typeId != PointTypeId.STEPS_SPECIFIED && typeId != PointTypeId.STEPS_TARGET && typeId != PointTypeId.STEPS_RANGE))
      return true;

    if (value && value != 'NaN') {
      return true;
    }

    return testContext.createError({
      message: ConstMessage.SELECTION_REQUIRED
    })
  }),
  // 付与対象
  target: yup.string().test((value, testContext) => {
    const categoryId: string = testContext.parent.categoryId;
    const typeId: string = testContext.parent.typeId;
    if (categoryId == '' || typeId == '' ||
      (typeId != PointTypeId.STEPS_SPECIFIED && typeId != PointTypeId.STEPS_TARGET && typeId != PointTypeId.STEPS_RANGE))
      return true;

    if (value && value != 'NaN') return true;

    return testContext.createError({
      message: ConstMessage.SELECTION_REQUIRED
    })
  }),
  // 指定/目標歩数
  _strSteps: yup.string().test((value, testContext) => {
    const categoryId: string = testContext.parent.categoryId;
    const typeId: string = testContext.parent.typeId;
    if (categoryId == '' || typeId == '' ||
      (typeId != PointTypeId.STEPS_SPECIFIED && typeId != PointTypeId.STEPS_TARGET))
      return true;

    return validNumber1orMore(value, testContext, true);
  }),
  // 歩数範囲
  // 歩数（下限）
  _strRangeStepsLower: yup.string().test((value, testContext) => {
    const categoryId: string = testContext.parent.categoryId;
    const typeId: string = testContext.parent.typeId;
    if (categoryId == '' || typeId == '' || typeId != PointTypeId.STEPS_RANGE) return true;

    return validNumber1orMore(value, testContext, true);
  }),
  // 歩数（上限）
  _strRangeStepsUpper: yup.string().test('custom-validation', '', function (value) {
    const trigger = this.options.context?.trigger;
    const categoryId: string = this.parent.categoryId;
    const typeId: string = this.parent.typeId;
    const stepLower: number = Number(this.parent._strRangeStepsLower);
    if (categoryId === '' || typeId === '' || typeId !== PointTypeId.STEPS_RANGE) return true;

    if (trigger !== 'execRangeStepsAdd' && isEmptyString(value)) return true;

    const result = validNumber1orMore(value, this, true);
    if (result === true && regexHalfNumMinus.test(this.parent._strRangeStepsLower) && Number(value) <= stepLower) {
      return this.createError({ message: `${stepLower + 1}以上の数字を入力してください。` });
    }

    return result;
  }),
  // 単位指定
  rangeStepsUnit: yup.string().test((value, testContext) => {
    const categoryId: string = testContext.parent.categoryId;
    const typeId: string = testContext.parent.typeId;
    if (categoryId == '' || typeId == '' || typeId != PointTypeId.STEPS_RANGE) return true;

    if (value) return true;

    return testContext.createError({
      message: ConstMessage.SELECTION_REQUIRED
    })
  }),
  // 歩数範囲開始年月日
  rangeStepsPeriodDateFrom: yup.string().test((value, testContext) => {
    const categoryId: string = testContext.parent.categoryId;
    const typeId: string = testContext.parent.typeId;
    const rangeStepsUnit: string = testContext.parent.rangeStepsUnit;
    if (categoryId == '' || typeId == '' || typeId != PointTypeId.STEPS_RANGE) return true;

    if (rangeStepsUnit != 'designation') return true;

    const dateFrom: Date = new Date(String(testContext.parent.rangeStepsPeriodDateFrom));
    if (!dateFrom || dateFrom.toString() == 'Invalid Date') {
      return testContext.createError({
        message: ConstMessage.ERR_NOT_INPUT
      });
    }
    return true;
  }),
  // 歩数範囲終了年月日
  rangeStepsPeriodDateTo: yup.string().test((value, testContext) => {
    const categoryId: string = testContext.parent.categoryId;
    const typeId: string = testContext.parent.typeId;
    const rangeStepsUnit: string = testContext.parent.rangeStepsUnit;
    if (categoryId == '' || typeId == '' || typeId != PointTypeId.STEPS_RANGE) return true;

    if (rangeStepsUnit != 'designation') return true;

    const dateFrom: Date = new Date(String(testContext.parent.rangeStepsPeriodDateFrom));
    const dateTo: Date = new Date(String(testContext.parent.rangeStepsPeriodDateTo));

    if (!dateTo || dateTo.toString() == 'Invalid Date') {
      return testContext.createError({
        message: ConstMessage.ERR_NOT_INPUT
      });
    }

    if (!(dateFrom && dateFrom.toString() != 'Invalid Date')) return true;

    if (dateFrom > dateTo) {
      return testContext.createError({
        message: "開始年月日より後を入力してください。"
      });
    }

    return true;
  }),
  // ステータス
  status: yup.string().test((value, testContext) => {
    const categoryId: string = testContext.parent.categoryId;
    const typeId: string = testContext.parent.typeId;
    if (categoryId == '' || typeId == '') return true;

    if (value) return true;

    return testContext.createError({
      message: ConstMessage.SELECTION_REQUIRED
    })
  }),
  // 有効期間（開始）
  validPeriodDateFrom: yup.string().test((value, testContext) => {
    const categoryId: string = testContext.parent.categoryId;
    const typeId: string = testContext.parent.typeId;
    const status: string = testContext.parent.status;
    if (categoryId == '' || typeId == '') return true;

    if (status != '1') return true;

    const dateFrom: Date = new Date(String(testContext.parent.validPeriodDateFrom));
    if (!dateFrom || dateFrom.toString() == 'Invalid Date') {
      return testContext.createError({
        message: ConstMessage.ERR_NOT_INPUT
      });
    }
    return true;
  }),
  // 有効期間（終了）
  validPeriodDateTo: yup.string().test((value, testContext) => {
    const categoryId: string = testContext.parent.categoryId;
    const typeId: string = testContext.parent.typeId;
    const status: string = testContext.parent.status;
    if (categoryId == '' || typeId == '') return true;

    if (status != '1') return true;

    const dateFrom: Date = new Date(String(testContext.parent.validPeriodDateFrom));
    const dateTo: Date = new Date(String(testContext.parent.validPeriodDateTo));

    if (!dateFrom || dateFrom.toString() == 'Invalid Date') return true;
    if (!dateTo || dateTo.toString() == 'Invalid Date') return true;

    if (dateFrom > dateTo) {
      return testContext.createError({
        message: "開始年月日より後を入力してください。"
      });
    }

    return true;
  }),
  // 付与回数制限
  // 付与回数制限設定有無
  numberLimitFlg: yup.string().test((value, testContext) => {
    const categoryId: string = testContext.parent.categoryId;
    const typeId: string = testContext.parent.typeId;

    if (categoryId == '' || typeId == '' || typeId == PointTypeId.STEPS_RANGE) return true;

    if (typeId == PointTypeId.VOLUNTEER_REGIST || typeId == PointTypeId.GROUP_REGIST || typeId == PointTypeId.FIRST_LOGIN) return true;

    if (!value) {
      return testContext.createError({
        message: ConstMessage.SELECTION_REQUIRED
      });
    }

    return true;
  }),
  // 付与回数制限設定
  numberLimitMap: yup.object().when(['categoryId', 'typeId', 'numberLimitFlg'], {
    is: (categoryId: string, typeId: string, numberLimitFlg: string) =>
      (categoryId != '')
      && (typeId != ''
        && typeId != PointTypeId.STEPS_RANGE
        && typeId != PointTypeId.VOLUNTEER_REGIST
        && typeId != PointTypeId.GROUP_REGIST
        && typeId != PointTypeId.FIRST_LOGIN)
      && (numberLimitFlg === 'true'),
    then: (schema) => schema.shape({
      // 制限回数
      _strNumberLimit: yup.string().test((value, testContext) => {
        return validNumber1orMore(value, testContext, true);
      }),
      // 単位指定
      period: yup.string().required(ConstMessage.SELECTION_REQUIRED),
      // 開始年月日
      dateFrom: yup.string().test((value, testContext) => {
        const period: string = testContext.parent.period;
        if (period != 'designation') return true;

        const dateFrom: Date = new Date(String(testContext.parent.dateFrom));
        if (!dateFrom || dateFrom.toString() == 'Invalid Date') {
          return testContext.createError({
            message: ConstMessage.ERR_NOT_INPUT
          });
        }
        return true;
      }),
      // 終了年月日
      dateTo: yup.string().test((value, testContext) => {
        const period: string = testContext.parent.period;
        if (period != 'designation') return true;

        const dateFrom: Date = new Date(String(testContext.parent.dateFrom));
        const dateTo: Date = new Date(String(testContext.parent.dateTo));
        if (!dateTo || dateTo.toString() == 'Invalid Date') {
          return testContext.createError({
            message: ConstMessage.ERR_NOT_INPUT
          });
        }

        if (!dateFrom || dateFrom.toString() == 'Invalid Date') return true;
        if (dateFrom > dateTo) {
          return testContext.createError({
            message: "開始年月日より後を入力してください。"
          });
        }
        return true;
      }),
    })
  }),
  // ポイント数合計上限設定有無
  pointLimitFlg: yup.string().test((value, testContext) => {
    const categoryId: string = testContext.parent.categoryId;
    const typeId: string = testContext.parent.typeId;

    if (categoryId == '' || typeId == '' || typeId == PointTypeId.STEPS_RANGE) return true;

    if (typeId == PointTypeId.VOLUNTEER_REGIST || typeId == PointTypeId.GROUP_REGIST || typeId == PointTypeId.FIRST_LOGIN) return true;

    if (!value) {
      return testContext.createError({
        message: ConstMessage.SELECTION_REQUIRED
      });
    }

    return true;
  }),
  // ポイント数合計上限設定
  pointLimitMap: yup.object().when(['categoryId', 'typeId', 'pointLimitFlg'], {
    is: (categoryId: string, typeId: string, pointLimitFlg: string) =>
      (categoryId != '')
      && (typeId != ''
        && typeId != PointTypeId.STEPS_RANGE
        && typeId != PointTypeId.VOLUNTEER_REGIST
        && typeId != PointTypeId.GROUP_REGIST
        && typeId != PointTypeId.FIRST_LOGIN)
      && (pointLimitFlg === 'true'),
    then: (schema) => schema.shape({
      // ポイント上限
      _strPointLimit: yup.string().test((value, testContext) => {
        return validNumber1orMore(value, testContext, true);
      }),
      // 単位指定
      period: yup.string().required(ConstMessage.SELECTION_REQUIRED),
      // 開始年月日
      dateFrom: yup.string().test((value, testContext) => {
        const period: string = testContext.parent.period;
        if (period != 'designation') return true;

        const dateFrom: Date = testContext.parent.dateFrom;
        if (!dateFrom || dateFrom.toString() == 'Invalid Date') {
          return testContext.createError({
            message: ConstMessage.ERR_NOT_INPUT
          });
        }
        return true;
      }),
      // 終了年月日
      dateTo: yup.string().test((value, testContext) => {
        const period: string = testContext.parent.period;
        if (period != 'designation') return true;

        const dateFrom: Date = new Date(String(testContext.parent.dateFrom));
        const dateTo: Date = new Date(String(testContext.parent.dateTo));
        if (!dateTo || dateTo.toString() == 'Invalid Date') {
          return testContext.createError({
            message: ConstMessage.ERR_NOT_INPUT
          });
        }

        if (!dateFrom || dateFrom.toString() == 'Invalid Date') return true;
        if (dateFrom > dateTo) {
          return testContext.createError({
            message: "開始年月日より後を入力してください。"
          });
        }
        return true;
      }),
    })
  }),
  // ボーナスポイント
  bonusSettingFlg: yup.string().test((value, testContext) => {
    const categoryId: string = testContext.parent.categoryId;
    const typeId: string = testContext.parent.typeId;
    if (categoryId == '' || typeId == '' || typeId == PointTypeId.VOLUNTEER_REGIST || typeId == PointTypeId.GROUP_REGIST || typeId == PointTypeId.FIRST_LOGIN) return true;

    const pointKind: string = testContext.parent.pointKind;
    if (((typeId == PointTypeId.STEPS_SPECIFIED
      || typeId == PointTypeId.STEPS_TARGET
      || typeId == PointTypeId.STEPS_RANGE)
      && pointKind != '1')
    ) return true;

    if ((typeId == PointTypeId.STEPS_SPECIFIED
      || typeId == PointTypeId.STEPS_TARGET
      || typeId == PointTypeId.STEPS_RANGE)
      && value == 'false') {
      return testContext.createError({
        message: ConstMessage.SELECTION_REQUIRED
      });
    }

    if (value == 'true') {
      const bonusSettingsLength: number = testContext.parent.bonusSettingsLength;
      if (bonusSettingsLength <= 0 || bonusSettingsLength == undefined) {
        return testContext.createError({
          message: `設定を追加してください`
        })
      }
    }
    return true;
  }),

});

export const PointAwardedEditModal = ({
  open,
  toggle,
  closeOnClickOutside,
  settings,
  bonusSettings,
  infoDataId,
  rangeStepsEditData
}: ModalProp & PointAwardedEditProps) => {

  // ログイン中の団体管理者情報
  const currentUser = auth.currentUser;

  const dispatch = useDispatch();

  const validationTriggerRef = useRef<string>('');

  const { control, handleSubmit, watch, setValue, getValues, reset, clearErrors, trigger } =
    useForm<PtAwardedSettings & { bonusSettingsLength: number }>({
      resolver: yupResolver(schema),
      context: { 
        get trigger() {
          return validationTriggerRef.current;
        },
      },
      mode: "all",
      defaultValues: {
        ...settings,
      }
    });

  const [pointCategoryList, setPointCategoryList] = useState<SelectItem[]>([]);
  const [pointTypeInitList, setPointTypeList] = useState<SelectItem[]>([]);
  const [pointTypeSelected, setPointTypeSelected] = useState<SelectItem[]>([]);
  const [typeIdDisabled, setTypeIdDisabled] = useState<boolean>();
  const [pointType0List, setPointType0List] = useState<SelectItem[]>([]);
  const [pointType1List, setPointType1List] = useState<SelectItem[]>([]);
  const [pointType2List, setPointType2List] = useState<SelectItem[]>([]);

  const [basePointDisabled, setBasePointDisabled] = useState<boolean>();
  const [isNumDateEnabled, setIsNumDateEnabled] = useState(false);
  const [isPtDateEnabled, setIsPtDateEnabled] = useState(false);
  const [target, setTarget] = useState<number | string | undefined>(settings?.target ? settings?.target : '');

  // ポイント種別
  const pointSubjectList = [
    { value: "0", name: "体重" },
    { value: "1", name: "血圧" },
    { value: "2", name: "体重と血圧" },
  ];

  // ポイント種別
  const pointKindList = [
    { value: "0", name: "通常" },
    { value: "1", name: "ボーナス" },
  ];
  // 付与対象
  const targetList = [
    { value: "0", name: "共通" },
    { value: "1", name: "男性" },
    { value: "2", name: "女性" },
  ];
  // ステータス
  const selectStatusItems = [
    { id: "disabled", title: "無効", value: "0" },
    { id: "enabled", title: "有効", value: "1" }
  ];
  const status = useInput('0');

  // 設定有無共通
  const selectFlgItems = [
    { id: "false", title: "設定なし", value: "false" },
    { id: "true", title: "設定あり", value: "true" }
  ];
  // 付与回数制限有無
  const numberLimitSelectFlgItems: RadioButtonItem[] = selectFlgItems.map((item) => {
    return {
      id: "numberLimit_" + item.id,
      title: item.title,
      value: item.value,
    }
  });
  const numberLimitFlg = useInput('false');

  // ポイント数合計上限有無
  const pointLimitSelectFlgItems: RadioButtonItem[] = selectFlgItems.map((item) => {
    return {
      id: "pointLimit_" + item.id,
      title: item.title,
      value: item.value,
    }
  });
  const pointLimitFlg = useInput('false');

  // ボーナスポイント
  const bonusSettingSelectFlgItems: RadioButtonItem[] = selectFlgItems.map((item) => {
    return {
      id: "bonusSetting_" + item.id,
      title: item.title,
      value: item.value,
    }
  });
  const bonusSettingFlg = useInput('false');

  // 単位指定
  const periodList = [
    { value: "", name: "" },
    { value: "none", name: "なし（無期限）" },
    { value: "day", name: "日" },
    { value: "week", name: "週" },
    { value: "month", name: "月" },
    { value: "year", name: "年" },
    { value: "designation", name: "期間" },
  ];

  // ボーナスポイント：追加押下
  const [isBonusPointSettingsModalOpen, setBonusPointSettingsModalOpen] = useState(false);
  const pointModalRef = useRef<{ resetForm: () => void }>(null);
  const openBonusPointSettingsModal = () => setBonusPointSettingsModalOpen(true);
  const closeBonusPointSettingsModal = () => setBonusPointSettingsModalOpen(false);
  const [bonusPointTableRowList, setBonusPointTableRowList] = useState<GridRow[]>([]);
  const [bonusPointSettingsList, setBonusPointSettingsList] = useState<PtBonusSettings[]>([]);
  const [bonusPointData, setBonusPointData] = useState<BonusPointSettings>();

  // ボーナスポイント：登録押下
  const handleBonusPointSettingsSubmit = (data: BonusPointSettings) => {
    if (data.no == 0) {
      bonusPointSettingsList.push({
        id: "",
        ptAwardedId: "",
        tenantId: "",
        categoryId: NaN,
        categoryName: "",
        typeId: "",
        typeName: "",
        name: "",
        status: -1,
        validPeriodDateFrom: undefined,
        validPeriodDateTo: undefined,
        bonusId: "",
        no: 0,
        condition: data.condition,
        _strNumOfDays: data._strNumOfDays,
        dateFrom: data.dateFrom !== "" ? new Date(data.dateFrom) : undefined,
        dateTo: data.dateTo !== "" ? new Date(data.dateTo) : undefined,
        _strNumOfTimes: data._strNumOfTimes,
        _strBonusPoints: data._strBonusPoints,
      });
    } else {
      const updateIndex = data.no - 1;
      bonusPointSettingsList[updateIndex].id = "";
      bonusPointSettingsList[updateIndex].ptAwardedId = "";
      bonusPointSettingsList[updateIndex].tenantId = "";
      bonusPointSettingsList[updateIndex].categoryId = NaN;
      bonusPointSettingsList[updateIndex].categoryName = "";
      bonusPointSettingsList[updateIndex].typeId = "";
      bonusPointSettingsList[updateIndex].typeName = "";
      bonusPointSettingsList[updateIndex].name = "";
      bonusPointSettingsList[updateIndex].no = data.no;
      bonusPointSettingsList[updateIndex].condition = data.condition;
      bonusPointSettingsList[updateIndex]._strNumOfDays = data._strNumOfDays;
      bonusPointSettingsList[updateIndex].dateFrom = data.dateFrom !== "" ? new Date(data.dateFrom) : undefined;
      bonusPointSettingsList[updateIndex].dateTo = data.dateTo !== "" ? new Date(data.dateTo) : undefined;
      bonusPointSettingsList[updateIndex]._strNumOfTimes = data._strNumOfTimes;
      bonusPointSettingsList[updateIndex]._strBonusPoints = data._strBonusPoints;
    }
    setBonusPointSettingsList(bonusPointSettingsList);
    setBonusPointTableRowList(createBonusPointTableRowList(bonusPointSettingsList));
    setValue("bonusSettingsLength", bonusPointSettingsList.length);
    closeBonusPointSettingsModal();
  };
  // ボーナスポイント一覧データ行の作成
  const createBonusPointTableRowList = (bonusSettings: PtBonusSettings[]) => {
    let tableRowList: GridRow[] = [];
    bonusSettings.map((data, index) => {
      tableRowList.push(
        createBonusPointTableRow(index, data.condition, data._strBonusPoints, data._strNumOfDays, data._strNumOfTimes, data.dateFrom, data.dateTo)
      )
    });
    return tableRowList;
  };
  /**
   * ボーナスポイント一覧 行データの作成
   * @param index
   * @param condition
   * @param bonusPoints
   * @param numOfDays
   * @param numOfTimes
   * @param dateFrom
   * @param dateTo
   * @returns
   */
  const createBonusPointTableRow = (
    index: number,
    condition: string,
    bonusPoints?: string,
    numOfDays?: string,
    numOfTimes?: string,
    dateFrom?: Date,
    dateTo?: Date
  ): GridRow => {
    return {
      rowData: {
        no: (<div className={"w-[3em] text-right"}>{index + 1}</div>),
        conditionName: bonusConditionName(condition, numOfDays, numOfTimes, dateFrom, dateTo),
        bonusPoint: (<div className={"w-[5rem] text-right"}>{bonusPoints}</div>),
        button: (
          <div className="flex space-x-2" style={{ display: "flex", justifyContent: "flex-end", marginRight: "10px" }}>
            <Button className="whitespace-nowrap"
              onClick={() => {
                setBonusPointData(editBonusPointData(index));
                openBonusPointSettingsModal();
              }} children={"編集"} />
            <Button color="red" onClick={() => deleteRow(index)} className="whitespace-nowrap" children={"削除"} />
          </div>
        ),
        index: index,
        condition: condition,
        _strNumOfDays: numOfDays,
        _strNumOfTimes: numOfTimes,
        dateFrom: dateFrom,
        dateTo: dateTo,
        _strBonusPoints: bonusPoints,
      }
    };
  };

  /**
   * ボーナスポイント削除 ボタン押下処理
   * @param delRow 削除行Index
   */
  const deleteRow = (delRow: number) => {
    bonusPointSettingsList.splice(delRow, 1);
    setBonusPointSettingsList(bonusPointSettingsList);
    setBonusPointTableRowList(createBonusPointTableRowList(bonusPointSettingsList));
    setValue("bonusSettingsLength", bonusPointSettingsList.length);
  };

  /**
   * ボーナスポイント一覧ヘッダ定義
   */
  const bonusPointTableColumns: GridCol[] = [
    { field: "no", headerName: "No.", style: "w-1/12", sort: false, },
    { field: "conditionName", headerName: "ボーナス条件", style: "w-3/12", sort: false, },
    { field: "_strBonusPoints", headerName: "ポイント数", style: "w-1/12", sort: false, },
    {
      field: "button",
      headerName: (
        <div style={{ display: "flex", justifyContent: "flex-end", marginRight: "10px" }}>
          <Button
            onClick={() => {
              setBonusPointData(initBonusPointData());
              openBonusPointSettingsModal();
            }}
            children="追加"
            disabled={bonusSettingFlg.value == "false"}
          />
        </div>
      ),
      style: "w-2/12", sort: false,
    },
  ];

  /**
   * ボーナスポイント 編集ボタン押下時の行データの設定
   *
   * @param selectRow 選択された行Index
   * @returns
   */
  const editBonusPointData = (selectRow: number): BonusPointSettings => {
    const selectRowData = bonusPointSettingsList[selectRow];
    return {
      no: selectRow + 1,
      condition: selectRowData.condition,
      _strNumOfDays: selectRowData._strNumOfDays ? selectRowData._strNumOfDays : "",
      dateFrom: selectRowData.dateFrom ? String(selectRowData.dateFrom) : "",
      dateTo: selectRowData.dateTo ? String(selectRowData.dateTo) : "",
      _strNumOfTimes: selectRowData._strNumOfTimes ? selectRowData._strNumOfTimes : "",
      _strBonusPoints: selectRowData._strBonusPoints ? selectRowData._strBonusPoints : "",
    };
  };
  /**
   * ボーナスポイント 追加ボタン押下時の設定値
   * @returns
   */
  const initBonusPointData = (): BonusPointSettings => {
    return {
      no: 0,
      condition: "",
      _strNumOfDays: "",
      dateFrom: "",
      dateTo: "",
      _strNumOfTimes: "",
      _strBonusPoints: "",
    };
  };

  /**
   * ボーナスポイント ボーナス条件名の変換
   * 
   * @param condition 条件設定
   * @param numOfDays 日数
   * @param numOfTimes 回数
   * @param dateFrom 開始年月日
   * @param dateTo 終了年月日
   * @returns ボーナス条件名
   */
  const bonusConditionName = (
    condition: string,
    numOfDays?: string,
    numOfTimes?: string,
    dateFrom?: string | Date,
    dateTo?: string | Date) => {
      switch (condition) {
        case 'seqDay':
          return BonusSettingsCondition.SEQ_DAY.replace('%1', `${numOfDays}`);
        case 'seqWeek':
          return BonusSettingsCondition.SEQ_WEEK.replace('%1', `${numOfDays}`);
        case 'seqMonth':
          return BonusSettingsCondition.SEQ_MONTH.replace('%1', `${numOfDays}`);
        case 'seqCount':
          return BonusSettingsCondition.SEQ_COUNT.replace('%1', `${numOfTimes}`);
        case 'periodDay':
          return BonusSettingsCondition.PERIOD_DAY.replace('%1', `${numOfDays}`).replace('%2', `${numOfTimes}`);
        case 'periodWeek':
          return BonusSettingsCondition.PERIOD_WEEK.replace('%1', `${numOfDays}`).replace('%2', `${numOfTimes}`);
        case 'periodMonth':
          return BonusSettingsCondition.PERIOD_MONTH.replace('%1', `${numOfDays}`).replace('%2', `${numOfTimes}`);
        case 'periodRange':
          if (typeof dateFrom === "string" && typeof dateTo === "string") {
            return BonusSettingsCondition.PERIOD_RANGE
              .replace('%1', `${dateFrom.replaceAll("-", "/")}`)
              .replace('%2', `${dateTo.replaceAll("-", "/")}`)
              .replace('%3', `${numOfTimes}`);
          } else {
            return BonusSettingsCondition.PERIOD_RANGE
              .replace('%1', `${convertDateToSlashTypeZeroPadString(dateFrom as Date)}`)
              .replace('%2', `${convertDateToSlashTypeZeroPadString(dateTo as Date)}`)
              .replace('%3', `${numOfTimes}`);
          }
        case 'sumCount':
          return BonusSettingsCondition.SUM_COUNT.replace('%1', `${numOfTimes}`);
        default:
          return "";
      }
  };

  /**
   * 初期処理
   */
  useEffect(() => {
    const f = async () => {
      // ポイントカテゴリを取得
      const ptCategoryMasterList = await pointDataQuery.getPointCategoryList();
      let ptCategoryList = [{ id: "", name: "" }];
      ptCategoryMasterList.map((item) => (ptCategoryList.push({ id: String(item.categoryId), name: item.categoryName })));
      setPointCategoryList(ptCategoryList);

      // ポイントタイプを取得
      const ptTypeMasterList = await pointDataQuery.getPointTypeList();
      let pointTypeInitList = [{ id: "", name: "" }];
      let ptTypeList0 = [{ id: "", name: "" }];
      let ptTypeList1 = [{ id: "", name: "" }];
      let ptTypeList2 = [{ id: "", name: "" }];
      ptTypeMasterList.map((item, index) => {
        if (item.categoryId == 0) {
          ptTypeList0.push({ id: item.typeId, name: item.typeName });
        } else if (item.categoryId == 1) {
          ptTypeList1.push({ id: item.typeId, name: item.typeName });
        } else if (item.categoryId == 2) {
          ptTypeList2.push({ id: item.typeId, name: item.typeName });
        }
      });
      // ポイントのリスト作成
      setPointType0List(ptTypeList0); // ボランティアポイント
      setPointType1List(ptTypeList1); // 各種ポイント
      setPointType2List(ptTypeList2); // アプリポイント

      if (settings === undefined) {
        // 追加：初期処理
        setValue("typeId", "");
        setTypeIdDisabled(true);
        setPointTypeList(pointTypeInitList);
        let rangeSteps: RangeStepsMap[] = [];
        rangeSteps.push({
          id: 0,
          _strRangeStepsLower: "",
          _strRangeStepsUpper: "",
          _strBasePoint: "",
        });
        setRangeStepsForm(rangeSteps);
      } else {
        // 編集：初期処理
        if (settings.categoryId == 0) {
          setPointTypeList(ptTypeList0);
          setPointTypeSelected(ptTypeList0);
        } else if (settings.categoryId == 1) {
          setPointTypeList(ptTypeList1);
          setPointTypeSelected(ptTypeList1);
        } else if (settings.categoryId == 2) {
          setPointTypeList(ptTypeList2);
          setPointTypeSelected(ptTypeList2);
        }
        setValue("name", settings.name);
        if (settings.typeId == PointTypeId.STEPS_RANGE && rangeStepsEditData) {
          setValue("_strBasePoint", rangeStepsEditData[rangeStepsEditData.length - 1].basePoint
            ? rangeStepsEditData[rangeStepsEditData.length - 1].basePoint?.toString() : "");
        } else {
          setValue("_strBasePoint", settings.basePoint ? settings.basePoint.toString() : "");
        }
        setValue("pointSubject", settings.pointSubject);
        setValue("_strRegistWtPoint", settings.registWtPoint ? settings.registWtPoint.toString() : "");
        setValue("_strRegistBpPoint", settings.registBpPoint ? settings.registBpPoint.toString() : "");
        setValue("_strRegistSamePoint", settings.registSamePoint ? settings.registSamePoint.toString() : "");
        setValue("_strActivityTime", settings.activityTime ? settings.activityTime.toString() : "");
        setValue("pointKind", settings.pointKind);
        setValue("target", settings.target);
        setTarget(settings.target);
        setValue("_strSteps", settings.steps ? settings.steps.toString() : "");
        // 歩数範囲
        setRangeStepsForm(rangeStepsEditData ?? []);
        setValue("rangeStepsMap", settings?.rangeStepsMap ?? []);
        if (rangeStepsEditData && rangeStepsEditData.length > 0) {
          setValue("_strRangeStepsLower", rangeStepsEditData[rangeStepsEditData.length - 1].rangeStepsLower
            ? rangeStepsEditData[rangeStepsEditData.length - 1].rangeStepsLower?.toString() : "");
          setValue("_strRangeStepsUpper", rangeStepsEditData[rangeStepsEditData.length - 1].rangeStepsUpper
            ? rangeStepsEditData[rangeStepsEditData.length - 1].rangeStepsUpper?.toString() : "");
        }
        setValue("rangeStepsPeriodDateFrom", settings?.rangeStepsPeriodDateFrom);
        setValue("rangeStepsPeriodDateTo", settings?.rangeStepsPeriodDateTo);
        setValue("rangeStepsUnit", settings?.rangeStepsUnit);
        // ステータス
        status.setValue(String(settings.status));
        setValue("status", settings.status);
        setValue("validPeriodDateFrom", settings?.validPeriodDateFrom);
        setValue("validPeriodDateTo", settings?.validPeriodDateTo);
        // 付与回数制限
        numberLimitFlg.setValue(String(settings.numberLimitFlg));
        if (settings.numberLimitMap) {
          setValue("numberLimitMap._strNumberLimit", settings.numberLimitMap.numberLimit ? settings.numberLimitMap.numberLimit.toString() : "");
          setValue("numberLimitMap.period", settings.numberLimitMap.period);
          setValue("numberLimitMap.dateFrom", settings.numberLimitMap.dateFrom);
          setValue("numberLimitMap.dateTo", settings.numberLimitMap.dateTo);
        }
        // ポイント数合計上限
        pointLimitFlg.setValue(String(settings.pointLimitFlg));
        if (settings.pointLimitMap) {
          setValue("pointLimitMap._strPointLimit", settings.pointLimitMap.pointLimit ? settings.pointLimitMap.pointLimit.toString() : "");
          setValue("pointLimitMap.period", settings.pointLimitMap.period);
          setValue("pointLimitMap.dateFrom", settings.pointLimitMap.dateFrom);
          setValue("pointLimitMap.dateTo", settings.pointLimitMap.dateTo);
        }
        bonusSettingFlg.setValue(String(settings.bonusSettingFlg));
        setValue("bonusSettingFlg", settings.bonusSettingFlg);
        if (bonusSettings !== undefined) {
          for (let i = 0; i < bonusSettings.length; i++) {
            // データをForm入力項目に設定
            bonusSettings[i]._strNumOfDays = bonusSettings[i].numOfDays ? bonusSettings[i].numOfDays?.toString() : "";
            bonusSettings[i]._strNumOfTimes = bonusSettings[i].numOfTimes ? bonusSettings[i].numOfTimes?.toString() : "";
            bonusSettings[i]._strBonusPoints = bonusSettings[i].bonusPoints ? bonusSettings[i].bonusPoints?.toString() : "";
          }
          setBonusPointSettingsList(bonusSettings);
          setBonusPointTableRowList(createBonusPointTableRowList(bonusSettings));
          setValue("bonusSettingsLength", bonusSettings.length);
        }

        if (open) {
          // 重複チェック初期化
          sessionStorage.removeItem("dupCheck");
        }
      }
    }
    f();
  }, [currentUser?.tenantId, settings?.id, bonusSettings]);

  /**
   * カテゴリ変更時
   * @param selectValue 選択された値
   */
  const onPointCategorySelect = (selectValue: any) => {
    // 選択値に応じてポイントタイプの選択肢を変更する
    setValue("typeId", "")
    setValue("categoryId", selectValue);
    setTypeIdDisabled(false);
    if (selectValue == '0') {
      setPointTypeList(pointType0List);
      setPointTypeSelected(pointType0List);
    } else if (selectValue == '1') {
      setPointTypeList(pointType1List);
      setPointTypeSelected(pointType1List);
    } else if (selectValue == '2') {
      setPointTypeList(pointType2List);
      setPointTypeSelected(pointType2List);
    } else {
      setTypeIdDisabled(true);
      setPointTypeList(pointTypeInitList);
      setPointTypeSelected(pointTypeInitList);
    }
    //バリデーションリセット
    reset({
      ...getValues(),
      categoryId: selectValue,
    });
  };

  /**
   * ポイントタイプ変更時の入力値初期化処理
   * (ポイントカテゴリ変更における、ポイントタイプクリア含む)
   */
  useEffect(() => {
    // ポイント名
    setValue("name", "");
    // ポイント数、活動時間、ポイント種別、ポイント数（体重登録）等
    setValue("_strBasePoint", "");
    setValue("_strActivityTime", "");
    setValue("pointKind", 0);
    setTarget("");
    setValue("target", 0);
    setValue("_strSteps", "");
    setValue("pointSubject", 0);
    setValue("_strRegistWtPoint", "");
    setValue("_strRegistBpPoint", "");
    setValue("_strRegistSamePoint", "");
    // 歩数範囲
    setRangeStepsForm([{
      id: 0,
      _strRangeStepsLower: "",
      _strRangeStepsUpper: "",
      _strBasePoint: ""
    }]);
    setValue("_strRangeStepsLower", "");
    setValue("_strRangeStepsUpper", "");
    setValue("rangeStepsPeriodDateFrom", new Date(""));
    setValue("rangeStepsPeriodDateTo", new Date(""));
    setValue("rangeStepsUnit", "");
    // ステータス
    status.setValue('0');
    setValue("status", 0);
    // 付与回数制限
    if (getValues("typeId") == PointTypeId.VOLUNTEER_REGIST ||
      getValues("typeId") == PointTypeId.GROUP_REGIST ||
      getValues("typeId") == PointTypeId.FIRST_LOGIN) {
      // ボランティア団体登録・団体登録・初回ログイン選択時の場合
      // 固定値を設定
      numberLimitFlg.setValue('true');
      setValue("numberLimitFlg", true);
      setValue("numberLimitMap._strNumberLimit", "1");
      setValue("numberLimitMap.period", "none");
    } else {
      numberLimitFlg.setValue('false');
      setValue("numberLimitFlg", false);
      setValue("numberLimitMap._strNumberLimit", "");
      setValue("numberLimitMap.period", "");
    }
    // ポイント数合計上限
    pointLimitFlg.setValue('false');
    setValue("pointLimitFlg", false);
    // ボーナスポイント
    setBonusPointTableRowList([]);
    setBonusPointSettingsList([]);
    setValue("bonusSettingsLength", 0);
    bonusSettingFlg.setValue('false');
    setValue("bonusSettingFlg", false)
    //バリデーションリセット
    reset({
      ...getValues(),
      name: "",
      _strBasePoint: "",
      _strActivityTime: "",
      pointKind: 0,
      target: 0,
      _strSteps: "",
      pointSubject: 0,
      _strRegistWtPoint: "",
      _strRegistBpPoint: "",
      _strRegistSamePoint: "",
      _strRangeStepsLower: "",
      _strRangeStepsUpper: "",
      rangeStepsPeriodDateFrom: new Date(""),
      rangeStepsPeriodDateTo: new Date(""),
      rangeStepsUnit: "",
      status: 0,
    });
  }, [watch("typeId")]);

  /**
   * ポイント種別が変更された場合
   * (ポイントカテゴリ変更における、ポイントタイプクリア時、ポイントタイプクリア変更時も含む)
   */
  useEffect(() => {
    const pointKind = getValues("pointKind");
    // 選択値に応じてポイント数のフィールドを変更する
    if (pointKind == 1) {
      setBasePointDisabled(true);
      setValue("_strBasePoint", "");
      clearErrors(["_strBasePoint"]);
      // ポイントタイプ：指定歩数到達、目標歩数到達の場合
      if ((getValues("typeId") == PointTypeId.STEPS_SPECIFIED || getValues("typeId") == PointTypeId.STEPS_TARGET)) {
        //付与制限回数とポイント数合計上限のラジオボタンを「設定なし」にする
        setValue("numberLimitFlg", false);
        numberLimitFlg.setValue('false');
        setValue("pointLimitFlg", false);
        pointLimitFlg.setValue('false');
        //ボーナスポイントのラジオボタンを「設定あり」にする
        bonusSettingFlg.setValue('true');
      } else if (getValues("typeId") == PointTypeId.STEPS_RANGE) {
        //ボーナスポイントのラジオボタンを「設定あり」にする
        bonusSettingFlg.setValue('true');
      }
    } else {
      setBasePointDisabled(false);
      //ボーナスポイントのラジオボタンを「設定なし」にする
      bonusSettingFlg.setValue('false');
    }
    if (getValues("typeId") == PointTypeId.STEPS_RANGE) {
      //歩数範囲を初期化
      setRangeStepsForm([{
        id: 0,
        _strRangeStepsLower: "",
        _strRangeStepsUpper: "",
        _strBasePoint: ""
      }]);
      setValue("_strRangeStepsLower", "");
      setValue("_strRangeStepsUpper", "");
      setValue("_strBasePoint", "");
      clearErrors(["_strRangeStepsLower", "_strRangeStepsUpper", "_strBasePoint"]);
    }
  }, [watch("pointKind")]);

  // ポイント対象変更時ポイント数リセット
  useEffect(() => {
    const typeId = getValues("typeId");
    const subject = getValues("pointSubject");

    if (typeId !== PointTypeId.VITAL_REGIST) return;

    (async () => {
      if (subject === 0) {
        setValue("_strRegistWtPoint", "");
        setValue("_strRegistBpPoint", "");
        setValue("_strRegistSamePoint", "");
        clearErrors(["_strRegistBpPoint", "_strRegistWtPoint"]);
      } else if (subject === 1) {
        setValue("_strRegistWtPoint", "");
        setValue("_strRegistBpPoint", "");
        setValue("_strRegistSamePoint", "");
        clearErrors(["_strRegistWtPoint", "_strRegistBpPoint"]);
      } else if (subject === 2) {
        setValue("_strRegistWtPoint", "");
        setValue("_strRegistBpPoint", "");
        clearErrors(["_strRegistWtPoint", "_strRegistBpPoint"]);
      }
    })();
  }, [watch("pointSubject")]);

  /**
   * 歩数範囲:単位指定
   */
  const [isRangesStepsDateEnabled, setIsRangesStepsDateEnabled] = useState(false);
  /**
   * 歩数範囲:単位指定変更時の処理
   */
  useEffect(() => {
    if (getValues("rangeStepsUnit") == 'designation') {
      setIsRangesStepsDateEnabled(true);
    } else {
      setIsRangesStepsDateEnabled(false);
      setValue("rangeStepsPeriodDateFrom", new Date(""));
      setValue("rangeStepsPeriodDateTo", new Date(""));
    }
  }, [watch("rangeStepsUnit")]);

  /**
   * ステータス有無変更時処理
   */
  useEffect(() => {
    clearErrors(["validPeriodDateFrom","validPeriodDateTo"]);
    setValue("status", status.value);
    if (status.value == 0) {
      setValue("validPeriodDateFrom", new Date(""));
      setValue("validPeriodDateTo", new Date(""));
    }
  }, [status.value]);

  // 付与回数制限有無変更時処理
  useEffect(() => {
    clearErrors(["numberLimitMap._strNumberLimit","numberLimitMap.period","numberLimitMap.dateFrom","numberLimitMap.dateTo"]);
    setValue("numberLimitFlg", numberLimitFlg.value);
    if (numberLimitFlg.value == 'false') {
      setIsNumDateEnabled(false);
      setValue("numberLimitMap._strNumberLimit", "");
      setValue("numberLimitMap.period", "");
      setValue("numberLimitMap.dateFrom", new Date(""));
      setValue("numberLimitMap.dateTo", new Date(""));
    }
  }, [numberLimitFlg.value]);

  //単位指定が期間の時のみ指定期間の入力を活性（付与回数制限）
  useEffect(() => {
    if (getValues("numberLimitMap.period") == 'designation') {
      setIsNumDateEnabled(true);
    } else {
      setIsNumDateEnabled(false);
      setValue("numberLimitMap.dateFrom", new Date(""));
      setValue("numberLimitMap.dateTo", new Date(""));
    }
  }, [watch("numberLimitMap.period")]);

  // ポイント数合計上限設定有無変更時処理
  useEffect(() => {
    clearErrors(["pointLimitMap._strPointLimit","pointLimitMap.period","pointLimitMap.dateFrom","pointLimitMap.dateTo"]);
    setValue("pointLimitFlg", pointLimitFlg.value);
    if (pointLimitFlg.value == 'false') {
      setIsPtDateEnabled(false);
      setValue("pointLimitMap._strPointLimit", "");
      setValue("pointLimitMap.period", "");
      setValue("pointLimitMap.dateFrom", new Date(""));
      setValue("pointLimitMap.dateTo", new Date(""));
    }
  }, [pointLimitFlg.value]);

  //単位指定が期間の時のみ指定期間の入力を活性（ポイント数合計上限）
  useEffect(() => {
    if (getValues("pointLimitMap.period") == 'designation') {
      setIsPtDateEnabled(true);
    } else {
      setIsPtDateEnabled(false);
      setValue("pointLimitMap.dateFrom", new Date(""));
      setValue("pointLimitMap.dateTo", new Date(""));
    }
  }, [watch("pointLimitMap.period")]);

  // ボーナスポイント設定有無変更時処理
  useEffect(() => {
    clearErrors(["bonusSettingFlg"]);
    setValue("bonusSettingFlg", bonusSettingFlg.value);
  }, [bonusSettingFlg.value]);

  // ポイント名変更時
  useEffect(() => {
    // dupCheckをリセット(false)
    sessionStorage.setItem("dupCheck", "false");
    // 入力値更新
    setValue("name",watch("name"));
  },[watch("name")]);

  //ステータス有効期間チェック時アラートのサブメッセージ
  const overlapData = (pointName:string, validPeriodDateFrom:Date|undefined, validPeriodDateTo:Date|undefined)=>{
      return `ポイント名：${pointName}　有効期間：${validPeriodDateFrom ? convertDateToSlashTypeString(validPeriodDateFrom) : ""}～${validPeriodDateTo ? convertDateToSlashTypeString(validPeriodDateTo) : ""}`;
  }

  //歩数範囲追加押下時
  const [rangeStepsForm, setRangeStepsForm] = useState<RangeStepsMap[]>([]);
  const rangeStepsAdd = async () => {
    let rangeSteps = rangeStepsForm[rangeStepsForm.length - 1];
    let hasError = false;
    validationTriggerRef.current = 'execRangeStepsAdd';

    // 歩数（下限）
    if (isEmptyString(rangeSteps._strRangeStepsLower)) {
      // 未入力
      trigger("_strRangeStepsLower");
      hasError = true;
    } else if (!regexHalfNumMinus.test(rangeSteps._strRangeStepsLower!) || !Number.isInteger(Number(rangeSteps._strRangeStepsLower))) {
      // 半角数字以外
      trigger("_strRangeStepsLower");
      hasError = true;
    } else if (Number(rangeSteps._strRangeStepsLower) <= 0) {
      // 0以下
      trigger("_strRangeStepsLower");
      hasError = true;
    }

    // 歩数（上限）
    if (isEmptyString(rangeSteps._strRangeStepsUpper)) {
      // 未入力
      await trigger("_strRangeStepsUpper");
      hasError = true;
    } else if (!regexHalfNumMinus.test(rangeSteps._strRangeStepsUpper!) || !Number.isInteger(Number(rangeSteps._strRangeStepsUpper))) {
      // 半角数字以外
      trigger("_strRangeStepsUpper");
      hasError = true;
    } else if (Number(rangeSteps._strRangeStepsUpper) <= 0) {
      // 0以下
      trigger("_strRangeStepsUpper");
      hasError = true;
    } else if (!isEmptyString(rangeSteps._strRangeStepsLower)
                 && regexHalfNumMinus.test(rangeSteps._strRangeStepsUpper!)
                 && Number(rangeSteps._strRangeStepsUpper) <= Number(rangeSteps._strRangeStepsLower)) {
      // 上限の値が下限より小さい（下限が未入力 or 不正値の場合はチェックしない）
      trigger("_strRangeStepsUpper");
      hasError = true;
    }

    // ポイント数
    if (isEmptyString(rangeSteps._strBasePoint)) {
      // 未入力
      trigger("_strBasePoint");
      hasError = true;
    } else if (!regexHalfNumMinus.test(rangeSteps._strBasePoint!) || !Number.isInteger(Number(rangeSteps._strBasePoint))) {
      // 半角数字以外
      trigger("_strBasePoint");
      hasError = true;
    } else if (Number(rangeSteps._strBasePoint) <= 0) {
      // 0以下
      trigger("_strBasePoint");
      hasError = true;
    }

    validationTriggerRef.current = '';

    if (hasError === false) {
      //　入力フォーム追加
      const formBody: RangeStepsMap = {
        id: rangeStepsForm.length,
        _strRangeStepsLower: Number(getValues("_strRangeStepsUpper")) ? (Number(getValues("_strRangeStepsUpper")) + 1).toString() : "",
        _strRangeStepsUpper: "",
        _strBasePoint: "",
      };

      //　初期値設定
      setValue("_strRangeStepsLower", formBody._strRangeStepsLower);
      setValue("_strRangeStepsUpper", formBody._strRangeStepsUpper);
      setValue("_strBasePoint", formBody._strBasePoint);
      setRangeStepsForm(prevForms => [...prevForms, formBody]);
      clearErrors(["_strRangeStepsLower", "_strRangeStepsUpper", "_strBasePoint"]);
    }
  };

  //歩数範囲削除押下時
  const rangeStepsDelete = (id: number) => {
    let rangeSteps = rangeStepsForm[rangeStepsForm.length - 2];
    setRangeStepsForm(prevForms => prevForms.filter(form => form.id !== id));
    setValue("_strRangeStepsLower", rangeSteps._strRangeStepsLower);
    setValue("_strRangeStepsUpper", rangeSteps._strRangeStepsUpper);
    setValue("_strBasePoint", rangeSteps._strBasePoint);
    clearErrors(["_strRangeStepsLower", "_strRangeStepsUpper", "_strBasePoint"]);
  };
  const handleInputChange = useCallback(
    (id: number, key: keyof RangeStepsMap) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setValue(key, e.target.value == '' ? undefined : Number(e.target.value))
        setRangeStepsForm(prevForms =>
          prevForms.map(form =>
            form.id === id ? { ...form, [key]: e.target.value } : form
          )
        );
      },
    []
  );

  /**
   * 登録ボタン押下時 処理
   * @param data
   */
  const onClickSubmit = async (data: PtAwardedSettings): Promise<void> => {
    try {
      //登録済みデータを取得（ポイント名が一致）
      const matchingNameData = await pointDataQuery.getPtAwardedSettingsByName(currentUser?.tenantId!, data.name);
      for (let i = 0; i < matchingNameData.length; i++) {
        //ポイント設定詳細編集データとID一致の場合
        if (matchingNameData[i].id == infoDataId){
          continue;
        }
        //ポイント名重複チェック
        if (matchingNameData[i].name == data.name){
          // 重複した場合、dupCheckをtrueにする
          sessionStorage.setItem("dupCheck", "true");
          return;
        }
      }
      //バイタル登録、初回ログイン、お知らせ閲覧、アプリ起動でステータスが有効の場合、有効期間をチェック（期間の重複なければ登録可能）
      if ((data.typeId == PointTypeId.VITAL_REGIST || data.typeId == PointTypeId.FIRST_LOGIN || data.typeId == PointTypeId.READ_MESSAGE || data.typeId == PointTypeId.LAUNCH_APP) && status.value == '1') {
        //登録済みデータを取得（ポイントタイプが一致、ステータス有効）
        const listData = await pointDataQuery.getPtAwardedSettingsByTypeId(currentUser?.tenantId!, data.typeId);
        const registeredData = listData.map((data)=>ptAwardedSettingsTimestampToDate(data));
        for (let i = 0; i < registeredData.length; i++) {
          //ポイント設定詳細編集データとID一致の場合
          if(registeredData[i].id == infoDataId){
            continue;
          }
          const registeredValidPeriodDateFrom = registeredData[i].validPeriodDateFrom ?? undefined;
          const registeredValidPeriodDateTo = registeredData[i].validPeriodDateTo ?? undefined;
          //どちらのデータも開始年月日・終了年月日あり
          if (data.validPeriodDateFrom && (data.validPeriodDateTo && ((data.validPeriodDateTo).toString() !== 'Invalid Date')) && registeredValidPeriodDateFrom && registeredValidPeriodDateTo) {
            if (new Date(data.validPeriodDateFrom) <= registeredValidPeriodDateTo && new Date(data.validPeriodDateTo) >= registeredValidPeriodDateFrom) {
              dispatch(alertOpen(AlertValue(ConstMessage.ERR_STATUS_OVERLAP, "danger",overlapData(registeredData[i].name,registeredValidPeriodDateFrom,registeredValidPeriodDateTo),)));
              return;
            }
            //入力データ：開始年月日・終了年月日あり　登録済みデータ：開始年月日のみ
          } else if (data.validPeriodDateFrom && (data.validPeriodDateTo && ((data.validPeriodDateTo).toString() !== 'Invalid Date')) && registeredValidPeriodDateFrom) {
            if (registeredValidPeriodDateFrom <= new Date(data.validPeriodDateTo)) {
              dispatch(alertOpen(AlertValue(ConstMessage.ERR_STATUS_OVERLAP, "danger" ,overlapData(registeredData[i].name,registeredValidPeriodDateFrom,undefined),)));
              return;
            }
            //入力データ：開始年月日のみ　登録済みデータ：開始年月日・終了年月日あり
          } else if (data.validPeriodDateFrom && registeredValidPeriodDateFrom && registeredValidPeriodDateTo) {
            if (new Date(data.validPeriodDateFrom) <= registeredValidPeriodDateTo) {
              dispatch(alertOpen(AlertValue(ConstMessage.ERR_STATUS_OVERLAP, "danger",overlapData(registeredData[i].name,registeredValidPeriodDateFrom,registeredValidPeriodDateTo),)));
              return;
            }
            //どちらのデータも開始年月日のみ
          } else {
            dispatch(alertOpen(AlertValue(ConstMessage.ERR_STATUS_OVERLAP, "danger" ,overlapData(registeredData[i].name,registeredValidPeriodDateFrom,undefined),)));
            return;
          }
        }
      }

      const now = new Date();  // 作成日時
      // Firestore登録
      data.tenantId = currentUser?.tenantId!;
      data.categoryId = Number(data.categoryId);
      data.categoryName = pointCategoryList.filter(item => item.id == String(data.categoryId)).at(0)?.name!;// カテゴリ名;
      data.typeName = pointTypeSelected.filter(item => item.id == String(data.typeId)).at(0)?.name!; // タイプ名
      if (data.typeId == PointTypeId.VITAL_REGIST) {
        // ポイント数
        delete data.basePoint;
        // ポイント種別
        data.pointSubject = Number(data.pointSubject);
        // ポイント数（体重登録）
        if (data._strRegistWtPoint != "") {
          data.registWtPoint = Number(data._strRegistWtPoint);
        } else {
          delete data.registWtPoint;
        }
        // ポイント数（血圧登録）
        if (data._strRegistBpPoint != "") {
          data.registBpPoint = Number(data._strRegistBpPoint);
        } else {
          delete data.registBpPoint;
        }
        // ポイント数（同時登録）
        if (data._strRegistSamePoint != "") {
          data.registSamePoint = Number(data._strRegistSamePoint);
        } else {
          delete data.registSamePoint;
        }
      } else {
        // ポイント数
        data.basePoint = Number(data._strBasePoint);
        // ポイント数（体重登録）
        delete data.registWtPoint;
        // ポイント数（血圧登録）
        delete data.registBpPoint;
        // ポイント数（同時登録）
        delete data.registSamePoint;
      }
      if (data.typeId == PointTypeId.VOLUNTEER_ACTIVITY) {
        // 活動時間
        if (data._strActivityTime != "") {
          data.activityTime = Number(data._strActivityTime);
        } else {
          delete data.activityTime;
        }
      } else {
        // 活動時間
        delete data.activityTime;
      }
      if (data.typeId == PointTypeId.STEPS_SPECIFIED || data.typeId == PointTypeId.STEPS_TARGET || data.typeId == PointTypeId.STEPS_RANGE) {
        // ポイント種別
        data.pointKind = Number(data.pointKind);
        // 付与対象
        data.target = Number(target);
        // 歩数
        if (data.typeId != PointTypeId.STEPS_RANGE) {
          data.steps = Number(data._strSteps);
        } else {
          delete data.steps;
        }
      } else {
        // ポイント種別
        data.pointKind = 0;
        // 付与対象
        delete data.target;
        // 歩数
        delete data.steps;
      }
      let stepRangeMap = {};
      // 歩数範囲
      if (data.typeId == PointTypeId.STEPS_RANGE) {
        if (data.pointKind == 1) {
          for (let i = 0; i < rangeStepsForm.length; i++) {
            let rangeSteps = rangeStepsForm[i];
            if (rangeSteps._strRangeStepsUpper !== "") {
              Object.assign(stepRangeMap, { [rangeSteps.id]:
                {
                  id: rangeSteps.id,
                  rangeStepsLower: Number(rangeSteps._strRangeStepsLower),
                  rangeStepsUpper: Number(rangeSteps._strRangeStepsUpper)
                }
              });
            } else {
              Object.assign(stepRangeMap, { [rangeSteps.id]:
                {
                  id: rangeSteps.id,
                  rangeStepsLower: Number(rangeSteps._strRangeStepsLower)
                }
              });
            }
          }
        } else {
          for (let i = 0; i < rangeStepsForm.length; i++) {
            let rangeSteps = rangeStepsForm[i];
            if (rangeSteps._strRangeStepsUpper !== "") {
              Object.assign(stepRangeMap, { [rangeSteps.id]:
                {
                  id: rangeSteps.id,
                  rangeStepsLower: Number(rangeSteps._strRangeStepsLower),
                  rangeStepsUpper: Number(rangeSteps._strRangeStepsUpper),
                  basePoint: Number(rangeSteps._strBasePoint)
                }
              });
            } else {
              Object.assign(stepRangeMap, { [rangeSteps.id]:
                {
                  id: rangeSteps.id,
                  rangeStepsLower: Number(rangeSteps._strRangeStepsLower),
                  basePoint: Number(rangeSteps._strBasePoint)
                }
              });
            }
          }
        }
        delete data.basePoint;
        delete data.rangeStepsLower;
        delete data.rangeStepsUpper;
        if (data.rangeStepsUnit == "designation") {
          data.rangeStepsPeriodDateFrom = new Date(String(data.rangeStepsPeriodDateFrom));
          data.rangeStepsPeriodDateTo = new Date(String(data.rangeStepsPeriodDateTo));
        } else {
          delete data.rangeStepsPeriodDateFrom;
          delete data.rangeStepsPeriodDateTo;
        }
      } else {
        delete data.rangeStepsMap;
        delete data.rangeStepsLower;
        delete data.rangeStepsUpper;
        delete data.rangeStepsUnit;
        delete data.rangeStepsPeriodDateFrom;
        delete data.rangeStepsPeriodDateTo;
      }
      // ステータス
      data.status = Number(status.value);
      if (data.status == 0) {
        delete data.validPeriodDateFrom;
        delete data.validPeriodDateTo;
      } else {
        data.validPeriodDateFrom = new Date(data.validPeriodDateFrom!);
        if (data.validPeriodDateTo && ((data.validPeriodDateTo as Date).toString() !== 'Invalid Date')) {
          data.validPeriodDateTo = new Date(data.validPeriodDateTo!);
        } else {
          delete data.validPeriodDateTo;
        }
      }
      // 付与回数制限
      data.numberLimitFlg = (numberLimitFlg.value === "true");
      if (!data.numberLimitFlg) {
        delete data.numberLimitMap;
      } else {
        if (data.typeId == PointTypeId.VOLUNTEER_REGIST
          || data.typeId == PointTypeId.GROUP_REGIST
          || data.typeId == PointTypeId.FIRST_LOGIN) {
          // ボランティア団体登録、団体登録、初回ログインの場合は固定値を設定
          data.numberLimitMap!.numberLimit = 1;
          data.numberLimitMap!.period = 'none';
          delete data.numberLimitMap!.dateFrom;
          delete data.numberLimitMap!.dateTo;
        } else {
          data.numberLimitMap!.numberLimit = Number(data.numberLimitMap!._strNumberLimit);
          if (data.numberLimitMap!.period == "designation") {
            data.numberLimitMap!.dateFrom = new Date(String(data.numberLimitMap!.dateFrom));
            data.numberLimitMap!.dateTo = new Date(String(data.numberLimitMap!.dateTo));
          } else {
            delete data.numberLimitMap!.dateFrom;
            delete data.numberLimitMap!.dateTo;
          }
        }
        delete data.numberLimitMap!._strNumberLimit;
      }
      // ポイント数合計上限
      data.pointLimitFlg = (pointLimitFlg.value === "true");
      if (!data.pointLimitFlg) {
        delete data.pointLimitMap;
      } else {
        data.pointLimitMap!.pointLimit = Number(data.pointLimitMap!._strPointLimit);
        if (data.pointLimitMap!.period == "designation") {
          data.pointLimitMap!.dateFrom = new Date(String(data.pointLimitMap!.dateFrom));
          data.pointLimitMap!.dateTo = new Date(String(data.pointLimitMap!.dateTo));
        } else {
          delete data.pointLimitMap!.dateFrom;
          delete data.pointLimitMap!.dateTo;
        }
        delete data.pointLimitMap!._strPointLimit;
      }
      // ボーナスポイント
      data.bonusSettingFlg = (bonusSettingFlg.value === "true");
      // 画面の入力値をボーナス設定登録用のリストに格納
      let saveBonusSettingList = bonusPointSettingsList;
      if (!data.bonusSettingFlg) {
        // ボーナス設定なしの場合は、ボーナス設定登録用のリストを初期化する
        saveBonusSettingList = [];
      }

      // ボーナスポイントの登録値([ポイント付与設定] の 同項目 を設定)の設定
      saveBonusSettingList.forEach((ptBonusSettings, index) => {
        ptBonusSettings.tenantId = data.tenantId;
        ptBonusSettings.categoryId = data.categoryId;
        ptBonusSettings.categoryName = data.categoryName;
        ptBonusSettings.typeId = data.typeId;
        ptBonusSettings.typeName = data.typeName;
        ptBonusSettings.name = data.name;
        ptBonusSettings.status = data.status;

        if (data.status == 0) {
          delete ptBonusSettings.validPeriodDateFrom;
          delete ptBonusSettings.validPeriodDateTo;
        } else {
          ptBonusSettings.validPeriodDateFrom = data.validPeriodDateFrom;
          if (data.validPeriodDateTo && ((data.validPeriodDateTo as Date).toString() !== 'Invalid Date')) {
            ptBonusSettings.validPeriodDateTo = data.validPeriodDateTo;
          } else {
            delete ptBonusSettings.validPeriodDateTo;
          }
        }

        // 新規ボーナスの場合はIDを生成
        if (ptBonusSettings.bonusId === "") {
          ptBonusSettings.bonusId = generateRandomDigits(10);
        }

        ptBonusSettings.no = index + 1;

        // Form入力値を登録データに設定（string ⇒ number）
        ptBonusSettings.numOfDays = Number(ptBonusSettings._strNumOfDays);
        ptBonusSettings.numOfTimes = Number(ptBonusSettings._strNumOfTimes);
        ptBonusSettings.bonusPoints = Number(ptBonusSettings._strBonusPoints);

        // 条件に応じた情報を登録するため、未設定フィールドは削除
        switch (ptBonusSettings.condition) {
          case 'seqDay':
          case 'seqWeek':
          case 'seqMonth':
            delete ptBonusSettings.numOfTimes;
            delete ptBonusSettings.dateFrom;
            delete ptBonusSettings.dateTo;
            break;
          case 'seqCount':
          case 'sumCount':
            delete ptBonusSettings.numOfDays;
            delete ptBonusSettings.dateFrom;
            delete ptBonusSettings.dateTo;
            break;
          case 'periodDay':
          case 'periodWeek':
          case 'periodMonth':
            delete ptBonusSettings.dateFrom;
            delete ptBonusSettings.dateTo;
            break;
          case 'periodRange':
            delete ptBonusSettings.numOfDays;
            break;
          default:
            break;
        }

        // Form入力値をデータ登録項目から削除
        delete ptBonusSettings._strNumOfDays;
        delete ptBonusSettings._strNumOfTimes;
        delete ptBonusSettings._strBonusPoints;
      });

      delete data._strBasePoint;
      delete data._strRegistWtPoint;
      delete data._strRegistBpPoint;
      delete data._strRegistSamePoint;

      delete data._strActivityTime;
      delete data._strSteps;

      delete data._strRangeStepsLower;
      delete data._strRangeStepsUpper;

      if (settings === undefined) {
        // 新規登録
        data.createUser = currentUser?.displayName!;
        data.createAt = now;
        data.updateAt = now;
        await pointDataQuery.registPtAwardedSettings(data, saveBonusSettingList, stepRangeMap);
      } else {
        // 編集
        data.updateAt = now;
        await pointDataQuery.updatePtAwardedSettings(data, saveBonusSettingList, stepRangeMap);
      }
      dispatch(alertOpen(AlertValue("登録しました。", "success")));
      toggle();
    } catch (err: any) {
      if (err === "ERR_NETWORK") {
        dispatch(
          alertOpen(AlertValue("ネットワークに接続できません。", "danger"))
        );
      } else {
        console.error(err)
        dispatch(alertOpen(AlertValue("登録に失敗しました", "danger")));
      }
    }
  };

  // バイタル登録 ポイント対象監視 ポイント数項目表示
  const typeId = watch("typeId");
  const pointSubject = Number(useWatch({ control, name: "pointSubject" }));
  const isWtVisible = typeId === PointTypeId.VITAL_REGIST && (pointSubject === 0 || pointSubject === 2);
  const isBpVisible = typeId === PointTypeId.VITAL_REGIST && (pointSubject === 1 || pointSubject === 2);
  const isSamePointVisible = typeId === PointTypeId.VITAL_REGIST && pointSubject === 2;

  return (
    <>
      <Modal
        open={open}
        toggle={toggle}
        closeOnClickOutside={closeOnClickOutside}
      >
        <div className="overflow-x-auto">
          <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all w-[calc(100vw_-_5rem)] min-w-[1280px] max-w-[1280px]">
            <div className="px-2 pb-4 p-4">
              <div className="mt-3">
                <Dialog.Title
                  as="h3"
                  className="mb-4 text-left text-lg font-medium leading-6 text-gray-900"
                >
                  ポイント設定
                </Dialog.Title>
                <div className="overflow-y-auto h-[calc(81vh_-_10rem)] max-h-[calc(100vh_-_15rem)]">
                  <div className="px-2 text-left  pb-8">
                    <div className="flex items-center gap-x-2 h-[5rem]">
                      {/* ポイントカテゴリ */}
                      <div className="mr-6">
                        <Controller
                          name={"categoryId"}
                          control={control}
                          render={({ field, fieldState }) => (
                            <div className="flex flex-col gap-y-1">
                              <label className="block text-sm font-medium text-gray-700">
                                {"ポイントカテゴリ"}
                                <span className="text-red-500 mx-1">*</span>
                              </label>
                              <SelectFields
                                {...field}
                                label=""
                                color="indigo"
                                className="h-[3.5rem]"
                                selectClassName={`${infoDataId ? "bg-gray-300" : ""} px-3 py-2 h-auto`}
                                onChange={(e: any) => { onPointCategorySelect(e.target.value); }}
                                value={getValues("categoryId")}
                                selectItems={pointCategoryList}
                                error={fieldState.error}
                                helperText={fieldState.error?.message}
                                style={infoDataId != undefined ? ({ 'width': '13rem', 'background-image': 'none' }) : { 'width': '13rem' }}
                                disabled={infoDataId != undefined}
                              />
                            </div>
                          )}
                        />
                      </div>
                      {/* ポイントタイプ */}
                      <div className="mr-6">
                        <Controller
                          name={"typeId"}
                          control={control}
                          render={({ field, fieldState }) => (
                            <div className="flex flex-col gap-y-1">
                              <label className="block text-sm font-medium text-gray-700">
                                {"ポイントタイプ"}
                                <span className="text-red-500 mx-1">*</span>
                              </label>
                              <SelectFields
                                {...field}
                                label=""
                                color="indigo"
                                className="h-[3.5rem]"
                                selectClassName={`${infoDataId || typeIdDisabled ? "bg-gray-300" : ""} px-3 py-2 h-auto`}
                                value={getValues("typeId")}
                                selectItems={pointTypeInitList}
                                disabled={typeIdDisabled || infoDataId != undefined}
                                error={fieldState.error}
                                helperText={fieldState.error?.message}
                                style={infoDataId != undefined ? ({ 'width': '13rem', 'background-image': 'none' }) : { 'width': '13rem' }}
                              />
                            </div>
                          )}
                        />
                      </div>
                      {getValues("typeId") == "" ? (
                        // ポイントタイプ未選択
                        <></>
                      ) : (
                        <div className="flex flex-col">
                          <h3 className="text-sm text-gray-700 mb-1">
                            {"ポイント名"}
                            <span className="text-red-500 mx-1">*</span>
                          </h3>
                          <Controller
                            name={"name"}
                            control={control}
                            render={({ field, fieldState }) => (
                              <TextField
                                {...field}
                                type={"text"}
                                label={""}
                                style={{ 'width': '37rem' }}
                                className={"h-[3.5rem]"}
                                maxLength={40}
                                error={fieldState.error || sessionStorage.getItem("dupCheck") === "true"}
                                helperText={fieldState.error?.message || (sessionStorage.getItem("dupCheck") === "true" ? ConstMessage.ERR_POINT_NAME_DUPLICATION : "")}
                              />
                            )}
                          />
                        </div>
                      )}
                    </div>
                    {getValues("typeId") == "" ? (
                      // ポイントタイプ未選択
                      <></>
                    ) : (
                      <>
                        <div className="flex gap-x-[2rem] mt-2">
                          {getValues("typeId") == PointTypeId.STEPS_RANGE ? (
                            <div className={`flex text-left grid items-start gap-x-4 grid-cols-4`}>
                              <div className="flex flex-col gap-y-1">
                                <h3 className="text-sm text-gray-700">
                                  {"ポイント種別"}
                                  <span className="text-red-500 mx-1">*</span>
                                </h3>
                                <Controller
                                  control={control}
                                  name={"pointKind"}
                                  render={({ field, fieldState }) => (
                                    <SelectFields
                                      {...field}
                                      label=""
                                      color="indigo"
                                      className="w-[8.2rem]"
                                      selectClassName={`${infoDataId ? "bg-gray-300" : ""} px-3 py-2 h-auto`}
                                      value={getValues("pointKind")}
                                      selectItems={pointKindList}
                                      error={fieldState.error}
                                      helperText={fieldState.error?.message}
                                      style={infoDataId != undefined ? ({ 'width': '7.5rem', 'background-image': 'none' }) : { 'width': '7.5rem' }}
                                      disabled={infoDataId != undefined}
                                    />
                                  )}
                                />
                              </div>
                              <Controller
                                name={"target"}
                                control={control}
                                render={({ field, fieldState }) => (
                                  <div className="flex flex-col gap-y-1">
                                    <h3 className="text-sm text-gray-700">
                                      {"付与対象"}
                                      <span className="text-red-500 mx-1">*</span>
                                    </h3>
                                    <SelectFields
                                      {...field}
                                      label=""
                                      color="indigo"
                                      className="w-[8.2rem]"
                                      selectClassName="px-3 py-2 h-auto"
                                      selectItems={targetList}
                                      error={fieldState.error}
                                      helperText={fieldState.error?.message}
                                      onChange={(e: any) => { setTarget(e.target.value); setValue("target", e.target.value); }}
                                      value={getValues("target")}
                                      style={{ 'width': '7.5rem' }}
                                    />
                                  </div>
                                )}
                              />
                            </div>
                          ) : (
                            <>{(getValues("typeId") == PointTypeId.STEPS_SPECIFIED || getValues("typeId") == PointTypeId.STEPS_TARGET) ? (
                              <div className={`flex text-left grid items-start gap-4 grid-cols-4`}>
                                <Controller
                                  name={"_strBasePoint"}
                                  control={control}
                                  render={({ field, fieldState }) => (
                                    <div className="flex flex-col gap-y-1">
                                      <div className="w-[8.5rem]">
                                        <h3 className="text-sm text-gray-700">
                                          {"ポイント数"}
                                          <span className="text-red-500 mx-1">{getValues("pointKind") != 1 ? "*" : ""}</span>
                                        </h3>
                                        <TextField
                                          {...field}
                                          type={"text"}
                                          label={""}
                                          color={"indigo"}
                                          className={"w-[8.2rem] mt-1"}
                                          inputClassName={`${basePointDisabled ? "bg-gray-300" : ""}`}
                                          style={{ 'text-align': 'right', 'width': '7.5rem' }}
                                          disabled={basePointDisabled}
                                          maxLength={5}
                                        />
                                      </div>
                                      {fieldState.error?.message && (
                                        <div className="block text-xs text-red-500 w-[8.5rem]">{fieldState.error.message}</div>
                                      )}
                                    </div>
                                  )}
                                />
                                <Controller
                                  control={control}
                                  name={"pointKind"}
                                  render={({ field, fieldState }) => (
                                    <div className="flex flex-col gap-y-1">
                                      <h3 className="text-sm text-gray-700">
                                        {"ポイント種別"}
                                        <span className="text-red-500 mx-1">*</span>
                                      </h3>
                                      <SelectFields
                                        {...field}
                                        label=""
                                        color="indigo"
                                        className="w-[8.2rem]"
                                        selectClassName={`${infoDataId ? "bg-gray-300" : ""} px-3 py-2 h-auto`}
                                        value={getValues("pointKind")}
                                        selectItems={pointKindList}
                                        error={fieldState.error}
                                        helperText={fieldState.error?.message}
                                        style={infoDataId != undefined ? ({ 'width': '7.5rem', 'background-image': 'none' }) : { 'width': '7.5rem' }}
                                        disabled={infoDataId != undefined}
                                      />
                                    </div>
                                  )}
                                />
                                <Controller
                                  name={"target"}
                                  control={control}
                                  render={({ field, fieldState }) => (
                                    <div className="flex flex-col gap-y-1">
                                      <h3 className="text-sm text-gray-700">
                                        {"付与対象"}
                                        <span className="text-red-500 mx-1">*</span>
                                      </h3>
                                      <SelectFields
                                        {...field}
                                        label=""
                                        color="indigo"
                                        className="w-[8.2rem]"
                                        selectClassName="px-3 py-2 h-auto"
                                        selectItems={targetList}
                                        error={fieldState.error}
                                        helperText={fieldState.error?.message}
                                        onChange={(e: any) => { setTarget(e.target.value); setValue("target", e.target.value); }}
                                        value={target}
                                        style={{ 'width': '7.5rem' }}
                                      />
                                    </div>
                                  )}
                                />
                                <Controller
                                  name={"_strSteps"}
                                  control={control}
                                  render={({ field, fieldState }) => (
                                    <div className="flex flex-col gap-y-1">
                                      <div className="w-[8.5rem]">
                                        <h3 className="text-sm text-gray-700">
                                          {getValues("typeId") == PointTypeId.STEPS_SPECIFIED ? "指定歩数" : "目標歩数/日"}
                                          <span className="text-red-500 mx-1">*</span>
                                        </h3>
                                        <TextField
                                          {...field}
                                          type={"text"}
                                          label={""}
                                          color={"indigo"}
                                          className={"w-[8.2rem] mt-1"}
                                          style={{ 'text-align': 'right', 'width': '7.5rem' }}
                                          maxLength={5}
                                        />
                                      </div>
                                      {fieldState.error?.message && (
                                        <div className="block text-xs text-red-500 w-[8.5rem]">{fieldState.error.message}</div>
                                      )}
                                    </div>
                                  )}
                                />
                              </div>
                            ) : (
                              <>{getValues("typeId") == PointTypeId.VITAL_REGIST ? (
                                  <div className="flex flex-wrap gap-x-8 items-start">
                                    <div className="flex flex-col gap-y-1 w-[8.5rem]">
                                      {/* ポイント対象 */}
                                      <h3 className="text-sm text-gray-700">ポイント対象</h3>
                                      <Controller
                                        name="pointSubject"
                                        control={control}
                                        render={({ field, fieldState }) => (
                                          <>
                                            <SelectFields
                                              {...field}
                                              label=""
                                              color="indigo"
                                              className="w-full"
                                              selectClassName="px-3 py-2 h-auto"
                                              value={getValues("pointSubject")}
                                              selectItems={pointSubjectList}
                                              error={fieldState.error}
                                              helperText={fieldState.error?.message}
                                              onChange={(e: { target: { value: any; }; }) => setValue('pointSubject', Number(e.target.value))}
                                            />
                                            {fieldState.error?.message && (
                                              <div className="text-xs text-red-500">{fieldState.error.message}</div>
                                            )}
                                          </>
                                        )}
                                      />
                                    </div>
                                    {/* 体重登録 */}
                                    {isWtVisible && (
                                      <div className="flex flex-col gap-y-1 w-[calc(25%-1.5rem)] min-w-[8.5rem]">
                                        <Controller
                                          name="_strRegistWtPoint"
                                          control={control}
                                          render={({ field, fieldState }) => (
                                            <>
                                              <h3 className="text-sm text-gray-700 whitespace-nowrap">
                                                {isSamePointVisible ? 'ポイント数（体重登録）' : 'ポイント数 '}
                                                <span className="text-red-500">*</span>
                                              </h3>
                                              <TextField
                                                {...field}
                                                type="text"
                                                className="w-[8.5rem] text-right"
                                                style={{ 'text-align': 'right' }}
                                                maxLength={5}
                                                color="indigo"
                                              />
                                              {fieldState.error?.message && (
                                                <div className="text-xs text-red-500">{fieldState.error.message}</div>
                                              )}
                                            </>
                                          )}
                                        />
                                      </div>
                                    )}
                                    {/* 血圧登録 */}
                                    {isBpVisible && (
                                      <div className="flex flex-col gap-y-1 w-[calc(25%-1.5rem)] min-w-[8.5rem]">
                                        <Controller
                                          name="_strRegistBpPoint"
                                          control={control}
                                          render={({ field, fieldState }) => (
                                            <>
                                              <h3 className="text-sm text-gray-700 whitespace-nowrap">
                                                {isSamePointVisible ? 'ポイント数（血圧登録）' : 'ポイント数 '}
                                                <span className="text-red-500">*</span>
                                              </h3>
                                              <TextField
                                                {...field}
                                                type="text"
                                                className="w-[8.5rem] text-right"
                                                style={{ 'text-align': 'right' }}
                                                maxLength={5}
                                                color="indigo"
                                              />
                                              {fieldState.error?.message && (
                                                <div className="text-xs text-red-500">{fieldState.error.message}</div>
                                              )}
                                            </>
                                          )}
                                        />
                                      </div>
                                    )}
                                    {/* 同時登録 */}
                                    {isSamePointVisible && (
                                      <div className="flex flex-col gap-y-1 w-[calc(25%-1.5rem)] min-w-[8.5rem]">
                                        <Controller
                                          name="_strRegistSamePoint"
                                          control={control}
                                          render={({ field, fieldState }) => (
                                            <>
                                              <h3 className="text-sm text-gray-700">ポイント数（同時登録）</h3>
                                              <TextField
                                                {...field}
                                                type="text"
                                                className="w-[8.5rem] text-right"
                                                style={{ 'text-align': 'right' }}
                                                maxLength={5}
                                                color="indigo"
                                              />
                                              {fieldState.error?.message && (
                                                <div className="text-xs text-red-500">{fieldState.error.message}</div>
                                              )}
                                            </>
                                          )}
                                        />
                                      </div>
                                    )}
                                  </div>
                                ) : (
                                <>{getValues("typeId") == PointTypeId.VOLUNTEER_ACTIVITY ? (
                                  <div className={`flex text-left grid items-start gap-4 grid-cols-4`}>
                                    <Controller
                                      name={"_strBasePoint"}
                                      control={control}
                                      render={({ field, fieldState }) => (
                                        <div className="flex flex-col gap-y-1">
                                          <div className="w-[8.5rem]">
                                            <h3 className="text-sm text-gray-700">
                                              {"ポイント数"}
                                              <span className="text-red-500 mx-1">*</span>
                                            </h3>
                                            <TextField
                                              {...field}
                                              type={"text"}
                                              label={""}
                                              color={"indigo"}
                                              className={"w-[8.2rem] mt-1"}
                                              style={{ 'text-align': 'right', 'width': '7.5rem' }}
                                              maxLength={5}
                                            />
                                          </div>
                                          {fieldState.error?.message && (
                                            <div className="block text-xs text-red-500 w-[8.5rem]">{fieldState.error.message}</div>
                                          )}
                                        </div>
                                      )}
                                    />
                                    <Controller
                                      name={"_strActivityTime"}
                                      control={control}
                                      render={({ field, fieldState }) => (
                                        <div className="flex flex-col gap-y-1">
                                          <div className="w-[8.5rem]">
                                            <h3 className="text-sm text-gray-700">
                                              {"活動時間（分）"}
                                              <span className="text-red-500 mx-1"></span>
                                            </h3>
                                            <TextField
                                              {...field}
                                              type={"text"}
                                              label={""}
                                              color={"indigo"}
                                              className={"w-[8.2rem] mt-1"}
                                              style={{ 'text-align': 'right', 'width': '5rem' }}
                                              maxLength={4}
                                            />
                                          </div>
                                          {fieldState.error?.message && (
                                            <div className="block text-xs text-red-500 w-[8.5rem]">{fieldState.error.message}</div>
                                          )}
                                        </div>
                                      )}
                                    />
                                  </div>
                                ) : (
                                  <div className={`text-left grid items-start gap-4 grid-cols-4`}>
                                    <Controller
                                      name={"_strBasePoint"}
                                      control={control}
                                      render={({ field, fieldState }) => (
                                        <div className="flex flex-col gap-y-1">
                                          <div className="w-[8.5rem]">
                                            <h3 className="text-sm text-gray-700">
                                              {"ポイント数"}
                                              <span className="text-red-500 mx-1">*</span>
                                            </h3>
                                            <TextField
                                              {...field}
                                              type={"text"}
                                              label={""}
                                              color={"indigo"}
                                              className={"w-[8.2rem] mt-1"}
                                              style={{ 'text-align': 'right', 'width': '7.5rem' }}
                                              maxLength={5}
                                            />
                                          </div>
                                          {fieldState.error?.message && (
                                            <div className="block text-xs text-red-500 w-[8.5rem]">{fieldState.error.message}</div>
                                          )}
                                        </div>
                                      )}
                                    />
                                  </div>
                                )}</>
                              )}</>
                            )}</>
                          )}
                        </div>
                        {/* ステータス */}
                        <div className="relative mt-10 shadow ring-1 ring-black ring-opacity-5 min-w-[37rem] max-w-[37rem]">
                          <div className="flex">
                            <span className={`absolute z-10 top-[-10px] left-[22px] text-sm text-gray-700 bg-white`}>
                              <label className="mb-1 block text-sm font-medium text-gray-700">
                                ステータス<span className="text-red-500 mx-1">*</span>
                              </label>
                            </span>
                          </div>
                          <div className="pl-5 pt-2 pb-5 pr-5">
                            <Controller
                              name={"status"}
                              control={control}
                              render={({ field, fieldState }) => (
                                <RadioButton
                                  children={undefined}
                                  name={"status"}
                                  radioButtonItem={selectStatusItems}
                                  value={status}
                                />
                              )}
                            />
                            <div className="ml-7 relative mt-5 shadow ring-1 ring-black ring-opacity-5 w-[32.5rem]">
                              <div className="flex">
                                <span className={`absolute z-10 top-[-10px] left-[22px] text-sm text-gray-700 bg-white`}>
                                  <label className="mb-1 block text-sm font-medium text-gray-700">
                                    有効期間
                                  </label>
                                </span>
                              </div>
                              <div className="flex mr-2 gap-x-3 ml-2">
                                <div className="h-full pt-5 pb-5">
                                  <Controller
                                    name={"validPeriodDateFrom"}
                                    control={control}
                                    render={({ field, fieldState }) => (
                                      <div className="flex flex-col gap-y-1">
                                        <div className="flex items-center">
                                          <h3 className="text-sm text-gray-900 text-end">
                                            {"開始年月日"}
                                            <span className="text-red-500 mx-1">*</span>
                                          </h3>
                                          <TextField
                                            {...field}
                                            type={"date"}
                                            label={""}
                                            color={"indigo"}
                                            className={"flex items-center gap-x-3 h-10"}
                                            selectClassName={"py-2 h-auto"}
                                            style={{ 'width': '8.5rem' }}
                                            disabled={!(status.value == 1)}
                                            inputClassName={`${status.value == 1 ? "" : "bg-gray-300"} mb-1`}
                                          />
                                        </div>
                                        {status.value == 1 && fieldState.error?.message && (
                                          <div className="block text-xs text-red-500" style={{ marginLeft: '98px' }}>{fieldState.error.message}</div>
                                        )}
                                      </div>
                                    )}
                                  />
                                </div>
                                <div className="h-full pt-5 pb-5">
                                  <Controller
                                    name={"validPeriodDateTo"}
                                    control={control}
                                    render={({ field, fieldState }) => (
                                      <div className="flex flex-col gap-y-1">
                                        <div className="flex items-center">
                                          <h3 className="text-sm text-gray-900 text-end">
                                            {"終了年月日"}
                                            <span className="text-red-500 mx-1"></span>
                                          </h3>
                                          <TextField
                                            {...field}
                                            type={"date"}
                                            label={""}
                                            color={"indigo"}
                                            className={"flex items-center gap-x-3 h-10"}
                                            selectClassName={"py-2 h-auto"}
                                            style={{ 'width': '8.5rem' }}
                                            disabled={!(status.value == 1)}
                                            inputClassName={`${status.value == 1 ? "" : "bg-gray-300"} mb-1`}
                                          />
                                        </div>
                                        {fieldState.error?.message && (
                                          <div className="block text-xs text-red-500 w-[8.5rem]" style={{ marginLeft: '98px' }}>{fieldState.error.message}</div>
                                        )}
                                      </div>
                                    )}
                                  />
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                        {getValues("typeId") == PointTypeId.STEPS_RANGE ? (
                          <>
                            {/* 歩数範囲毎 */}
                            <div className="relative mt-5 shadow ring-1 ring-black ring-opacity-5 min-w-[54rem] max-w-[54rem]">
                              <div className="flex">
                                <span className={`absolute z-10 top-[-10px] left-[22px] text-sm text-gray-700 bg-white`}>
                                  <label className="mb-1 block text-sm font-medium text-gray-700">
                                    歩数範囲<span className="text-red-500 mx-1">*</span>
                                  </label>
                                </span>
                              </div>
                              <div className="pl-5 pt-2 pb-5 pr-5">
                                {rangeStepsForm.map((form, index) => (
                                  <div className="flex pt-2 ml-7">
                                    <Controller
                                      name={"_strRangeStepsLower"}
                                      control={control}
                                      render={({ field, fieldState }) => (
                                        <div className="flex flex-col gap-y-1">
                                          <div className="flex items-center">
                                            <h3 className="text-sm text-gray-900 text-end w-[5rem]">
                                              {`範囲 ${index + 1}`}
                                              <span className="text-red-500 mx-1">*</span>
                                            </h3>
                                            <TextField
                                              {...field}
                                              type={"text"}
                                              label={""}
                                              color={"indigo"}
                                              className={"flex items-center gap-x-3 h-10"}
                                              selectClassName={"py-2 h-auto"}
                                              style={{ 'width': '8.5rem', 'text-align': 'right' }}
                                              value={form._strRangeStepsLower}
                                              onChange={handleInputChange(Number(form.id), '_strRangeStepsLower')}
                                              disabled={rangeStepsForm.length > index + 1 || index != 0}
                                              inputClassName={`${rangeStepsForm.length > index + 1 || index != 0 ? "bg-gray-300" : ""}`}
                                            />
                                            <span>　～　</span>
                                          </div>
                                          {fieldState.error?.message && rangeStepsForm.length == index + 1 && (
                                            <div className="block text-xs text-red-500 w-[8.5rem]" style={{ marginLeft: '90px' }}>{fieldState.error.message}</div>
                                          )}
                                        </div>
                                      )}
                                    />
                                    <Controller
                                      name={"_strRangeStepsUpper"}
                                      control={control}
                                      render={({ field, fieldState }) => (
                                        <div className="flex flex-col gap-y-1">
                                          <div className="flex items-center">
                                            <TextField
                                              {...field}
                                              type={"text"}
                                              label={""}
                                              color={"indigo"}
                                              className={"flex items-center h-10"}
                                              selectClassName={"py-2 h-auto"}
                                              style={{ 'width': '8.5rem', 'text-align': 'right' }}
                                              value={form._strRangeStepsUpper}
                                              onChange={handleInputChange(Number(form.id), '_strRangeStepsUpper')}
                                              disabled={rangeStepsForm.length > index + 1}
                                              inputClassName={`${rangeStepsForm.length > index + 1 ? "bg-gray-300" : ""}`}
                                            />
                                          </div>
                                          {fieldState.error?.message && rangeStepsForm.length == index + 1 && (
                                            <div className="block text-xs text-red-500 w-[8.5rem]" style={{ marginLeft: '' }}>{fieldState.error.message}</div>
                                          )}
                                        </div>
                                      )}
                                    />
                                    <Controller
                                      name={"_strBasePoint"}
                                      control={control}
                                      render={({ field, fieldState }) => (
                                        <div className="flex flex-col gap-y-1 ml-8">
                                          <div className="flex items-center">
                                            <h3 className="text-sm text-gray-700">
                                              {"ポイント数"}
                                              <span className="text-red-500 mx-1">{getValues("pointKind") != 1 ? "*" : ""}</span>
                                            </h3>
                                            <TextField
                                              {...field}
                                              type={"text"}
                                              label={""}
                                              color={"indigo"}
                                              className={`${getValues("pointKind") == 1 ? "ml-[0.4rem]" : ""} flex items-center gap-x-3 h-10`}
                                              inputClassName={`${(basePointDisabled || rangeStepsForm.length > index+1) || basePointDisabled ? "bg-gray-300" : ""}`}
                                              style={{ 'text-align': 'right', 'width': '7rem' }}
                                              value={form._strBasePoint}
                                              onChange={handleInputChange(Number(form.id), '_strBasePoint')}
                                              maxLength={5}
                                              disabled={basePointDisabled || rangeStepsForm.length > index+1}
                                            />
                                          </div>
                                          <div className="w-[8.5rem]">
                                            {fieldState.error?.message && rangeStepsForm.length == index + 1 && (
                                              <div className="block text-xs text-red-500 w-[8.5rem]" style={{ marginLeft: '95px' }}>{fieldState.error.message}</div>
                                            )}
                                          </div>
                                        </div>
                                      )}
                                    />
                                    {form.id == 0 ? (
                                      <Button
                                        size="sm"
                                        onClick={rangeStepsAdd}
                                        children={"範囲追加"}
                                        className={"flex items-center ml-5 h-10"}
                                        disabled={getValues("pointKind") == 1}
                                      />
                                    ) : (
                                      <Button
                                        size="sm"
                                        onClick={() => rangeStepsDelete(Number(form.id))}
                                        children={"削除"}
                                        className={"flex items-center ml-5 h-10"}
                                        color={"red"}
                                        disabled={rangeStepsForm.length > index + 1}
                                      />
                                    )}
                                  </div>
                                ))}
                                <div className="flex items-center mt-5 ml-7">
                                  <Controller
                                    name={"rangeStepsUnit"}
                                    control={control}
                                    render={({ field, fieldState }) => (
                                      <div className="flex items-center">
                                        <h3 className="text-sm text-gray-900  w-[5rem] text-end">
                                          {"単位指定"}
                                          <span className="text-red-500 mx-1">*</span>
                                        </h3>
                                        <SelectFields
                                          {...field}
                                          color="indigo"
                                          className="h-10 flex items-center gap-x-3"
                                          label={""}
                                          selectClassName="px-3 py-2 h-auto mb-1"
                                          selectItems={(periodList.filter(periodList => periodList.name != "なし（無期限）" && periodList.name != "年"))}
                                          style={{ 'width': '10rem' }}
                                        />
                                        {fieldState.error?.message && (
                                          <div className="block text-xs text-red-500" style={{ marginLeft: '5px' }}>{fieldState.error.message}</div>
                                        )}
                                      </div>
                                    )}
                                  />
                                </div>
                                <div className="ml-7 relative mt-5 shadow ring-1 ring-black ring-opacity-5 w-[32.5rem]">
                                  <div className="flex">
                                    <span className={`absolute z-10 top-[-10px] left-[22px] text-sm text-gray-700 bg-white`}>
                                      <label className="mb-1 block text-sm font-medium text-gray-700">
                                        指定期間
                                      </label>
                                    </span>
                                  </div>
                                  <div className="flex mr-2 gap-x-3 ml-2">
                                    <div className="h-full pt-5 pb-5">
                                      <Controller
                                        name={"rangeStepsPeriodDateFrom"}
                                        control={control}
                                        render={({ field, fieldState }) => (
                                          <div className="flex flex-col gap-y-1">
                                            <div className="flex items-center">
                                              <h3 className="text-sm text-gray-900 text-end">
                                                {"開始年月日"}
                                                <span className="text-red-500 mx-1">*</span>
                                              </h3>
                                              <TextField
                                                {...field}
                                                type={"date"}
                                                label={""}
                                                color={"indigo"}
                                                className={"flex items-center gap-x-3 h-10"}
                                                selectClassName={"py-2 h-auto"}
                                                style={{ 'width': '8.5rem' }}
                                                disabled={isRangesStepsDateEnabled ? false : true}
                                                inputClassName={`${isRangesStepsDateEnabled ? "" : "bg-gray-300"} mb-1`}
                                              />
                                            </div>
                                            {getValues("rangeStepsUnit") == 'designation' && fieldState.error?.message && (
                                              <div className="block text-xs text-red-500" style={{ marginLeft: '98px' }}>{fieldState.error.message}</div>
                                            )}
                                          </div>
                                        )}
                                      />
                                    </div>
                                    <div className="h-full pt-5 pb-5">
                                      <Controller
                                        name={"rangeStepsPeriodDateTo"}
                                        control={control}
                                        render={({ field, fieldState }) => (
                                          <div className="flex flex-col gap-y-1">
                                            <div className="flex items-center">
                                              <h3 className="text-sm text-gray-900 text-end">
                                                {"終了年月日"}
                                                <span className="text-red-500 mx-1">*</span>
                                              </h3>
                                              <TextField
                                                {...field}
                                                type={"date"}
                                                label={""}
                                                color={"indigo"}
                                                className={"flex items-center gap-x-3 h-10"}
                                                selectClassName={"py-2 h-auto"}
                                                style={{ 'width': '8.5rem' }}
                                                disabled={isRangesStepsDateEnabled ? false : true}
                                                inputClassName={`${isRangesStepsDateEnabled ? "" : "bg-gray-300"} mb-1`}
                                              />
                                            </div>
                                            {getValues("rangeStepsUnit") == 'designation' && fieldState.error?.message && (
                                              <div className="block text-xs text-red-500 w-[8.5rem]" style={{ marginLeft: '98px' }}>{fieldState.error.message}</div>
                                            )}
                                          </div>
                                        )}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </>
                        ) : (
                          <></>
                        )}
                        {getValues("typeId") == PointTypeId.STEPS_RANGE ? (
                          <></>
                        ) : (
                          <>
                            <div className="flex items-center gap-x-[2rem]">
                              {/* 付与回数制限 */}
                              <div className="relative mt-5 shadow ring-1 ring-black ring-opacity-5 min-w-[37rem] max-w-[37rem]">
                                <div className="flex">
                                  <span className={`absolute z-10 top-[-10px] left-[22px] text-sm text-gray-700 bg-white`}>
                                    <label className="mb-1 block text-sm font-medium text-gray-700">
                                      付与回数制限<span className="text-red-500 mx-1">*</span>
                                    </label>
                                  </span>
                                </div>
                                <div className="pl-5 pt-2 pb-5 pr-5">
                                  <Controller
                                    name={"numberLimitFlg"}
                                    control={control}
                                    render={({ field, fieldState }) => (
                                      <RadioButton
                                        children={undefined}
                                        name={"numberLimitFlg"}
                                        value={numberLimitFlg}
                                        radioButtonItem={numberLimitSelectFlgItems}
                                        disabled={(getValues("typeId") == PointTypeId.VOLUNTEER_REGIST || getValues("typeId") == PointTypeId.GROUP_REGIST || getValues("typeId") == PointTypeId.FIRST_LOGIN) || ((getValues("typeId") == PointTypeId.STEPS_SPECIFIED || getValues("typeId") == PointTypeId.STEPS_TARGET) && getValues("pointKind") == 1)}
                                      />
                                    )}
                                  />
                                  <div className="ml-7">
                                    <Controller
                                      name={"numberLimitMap._strNumberLimit"}
                                      control={control}
                                      render={({ field, fieldState }) => (
                                        <div className="flex items-center mt-5">
                                          <h3 className="text-sm text-gray-900 text-end">
                                            {"制限回数"}
                                            <span className="text-red-500 mx-1">*</span>
                                          </h3>
                                          <TextField
                                            {...field}
                                            type={"text"}
                                            label={""}
                                            color={"indigo"}
                                            className={"flex items-center gap-x-3 h-10"}
                                            selectClassName={"py-2 h-auto"}
                                            style={{ 'width': '5rem', 'text-align': 'right' }}
                                            inputClassName={`${(numberLimitFlg.value == "true") && (getValues("typeId") != PointTypeId.VOLUNTEER_REGIST && getValues("typeId") != PointTypeId.GROUP_REGIST && getValues("typeId") != PointTypeId.FIRST_LOGIN) ? "" : "bg-gray-300"} mb-1`}
                                            disabled={(!(numberLimitFlg.value == "true")) || (getValues("typeId") == PointTypeId.VOLUNTEER_REGIST || getValues("typeId") == PointTypeId.GROUP_REGIST || getValues("typeId") == PointTypeId.FIRST_LOGIN)}
                                            maxLength={3}
                                          />
                                          {numberLimitFlg.value == "true" && fieldState.error?.message && (
                                            <div className="block text-xs text-red-500" style={{ marginLeft: '5px' }}>{fieldState.error.message}</div>
                                          )}
                                        </div>
                                      )}
                                    />
                                    <div className="flex items-center mt-5">
                                      <Controller
                                        name={"numberLimitMap.period"}
                                        control={control}
                                        render={({ field, fieldState }) => (
                                          <div className="flex items-center">
                                            <h3 className="text-sm text-gray-900 text-end">
                                              {"単位指定"}
                                              <span className="text-red-500 mx-1">*</span>
                                            </h3>
                                            <SelectFields
                                              {...field}
                                              color="indigo"
                                              className="h-10 flex items-center gap-x-3"
                                              label={""}
                                              selectClassName={`${(numberLimitFlg.value == "true") && (getValues("typeId") != PointTypeId.VOLUNTEER_REGIST && getValues("typeId") != PointTypeId.GROUP_REGIST && getValues("typeId") != PointTypeId.FIRST_LOGIN) ? "" : "bg-gray-300"} px-3 py-2 h-auto mb-1`}
                                              selectItems={
                                                getValues("typeId") === PointTypeId.VITAL_REGIST || getValues("typeId") === PointTypeId.STEPS_SPECIFIED || getValues("typeId") === PointTypeId.STEPS_TARGET
                                                  ? periodList.filter(item =>
                                                      item.name !== "日"
                                                    )
                                                  : (getValues("typeId") === PointTypeId.STEPS_SPECIFIED || getValues("typeId") === PointTypeId.STEPS_TARGET)
                                                  ? periodList.filter(item => item.name !== "年")
                                                  : periodList
                                              }
                                              disabled={(!(numberLimitFlg.value == "true")) || (getValues("typeId") == PointTypeId.VOLUNTEER_REGIST || getValues("typeId") == PointTypeId.GROUP_REGIST || getValues("typeId") == PointTypeId.FIRST_LOGIN)}
                                              style={{ 'width': '10rem' }}
                                            />
                                            {numberLimitFlg.value == "true" && fieldState.error?.message && (
                                              <div className="block text-xs text-red-500" style={{ marginLeft: '5px' }}>{fieldState.error.message}</div>
                                            )}
                                          </div>
                                        )}
                                      />
                                    </div>
                                  </div>
                                  <div className="ml-7 relative mt-5 shadow ring-1 ring-black ring-opacity-5 w-[32.5rem]">
                                    <div className="flex">
                                      <span className={`absolute z-10 top-[-10px] left-[22px] text-sm text-gray-700 bg-white`}>
                                        <label className="mb-1 block text-sm font-medium text-gray-700">
                                          指定期間
                                        </label>
                                      </span>
                                    </div>
                                    <div className="flex mr-2 gap-x-3 ml-2">
                                      <div className="h-full pt-5 pb-5">
                                        <Controller
                                          name={"numberLimitMap.dateFrom"}
                                          control={control}
                                          render={({ field, fieldState }) => (
                                            <div className="flex flex-col gap-y-1">
                                              <div className="flex items-center">
                                                <h3 className="text-sm text-gray-900 text-end">
                                                  {"開始年月日"}
                                                  <span className="text-red-500 mx-1">*</span>
                                                </h3>
                                                <TextField
                                                  {...field}
                                                  type={"date"}
                                                  label={""}
                                                  color={"indigo"}
                                                  className={"flex items-center gap-x-3 h-10"}
                                                  selectClassName={"py-2 h-auto"}
                                                  style={{ 'width': '8.5rem' }}
                                                  disabled={(numberLimitFlg.value == 'true') ? (isNumDateEnabled ? false : true) : true}
                                                  inputClassName={`${(numberLimitFlg.value == 'true') ? (isNumDateEnabled ? "" : "bg-gray-300") : "bg-gray-300"}  mb-1`}
                                                />
                                              </div>
                                              {isNumDateEnabled && fieldState.error?.message && (
                                                <div className="block text-xs text-red-500" style={{ marginLeft: '98px' }}>{fieldState.error.message}</div>
                                              )}
                                            </div>
                                          )}
                                        />
                                      </div>
                                      <div className="h-full pt-5 pb-5">
                                        <Controller
                                          name={"numberLimitMap.dateTo"}
                                          control={control}
                                          render={({ field, fieldState }) => (
                                            <div className="flex flex-col gap-y-1">
                                              <div className="flex items-center">
                                                <h3 className="text-sm text-gray-900 text-end">
                                                  {"終了年月日"}
                                                  <span className="text-red-500 mx-1">*</span>
                                                </h3>
                                                <TextField
                                                  {...field}
                                                  type={"date"}
                                                  label={""}
                                                  color={"indigo"}
                                                  className={"flex items-center gap-x-3 h-10"}
                                                  selectClassName={"py-2 h-auto"}
                                                  style={{ 'width': '8.5rem' }}
                                                  disabled={(numberLimitFlg.value == 'true') ? (isNumDateEnabled ? false : true) : true}
                                                  inputClassName={`${(numberLimitFlg.value == 'true') ? (isNumDateEnabled ? "" : "bg-gray-300") : "bg-gray-300"}  mb-1`}
                                                />
                                              </div>
                                              {isNumDateEnabled && fieldState.error?.message && (
                                                <div className="block text-xs text-red-500 w-[8.5rem]" style={{ marginLeft: '98px' }}>{fieldState.error.message}</div>
                                              )}
                                            </div>
                                          )}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                              {/* ポイント数合計上限 */}
                              <div className="relative mt-5 shadow ring-1 ring-black ring-opacity-5 min-w-[37rem] max-w-[37rem]">
                                <div className="flex">
                                  <span className={`absolute z-10 top-[-10px] left-[22px] text-sm text-gray-700 bg-white`}>
                                    <label className="mb-1 block text-sm font-medium text-gray-700">
                                      ポイント数合計上限<span className="text-red-500 mx-1">*</span>
                                    </label>
                                  </span>
                                </div>
                                <div className="pl-5 pt-2 pb-5 pr-5">
                                  <Controller
                                    name={"pointLimitFlg"}
                                    control={control}
                                    render={({ field, fieldState }) => (
                                      <RadioButton
                                        children={undefined}
                                        name={"pointLimitFlg"}
                                        value={pointLimitFlg}
                                        radioButtonItem={pointLimitSelectFlgItems}
                                        disabled={(getValues("typeId") == PointTypeId.VOLUNTEER_REGIST || getValues("typeId") == PointTypeId.GROUP_REGIST || getValues("typeId") == PointTypeId.FIRST_LOGIN) || ((getValues("typeId") == PointTypeId.STEPS_SPECIFIED || getValues("typeId") == PointTypeId.STEPS_TARGET) && getValues("pointKind") == 1)}
                                      />
                                    )}
                                  />
                                  <div className="ml-7">
                                    <Controller
                                      name={"pointLimitMap._strPointLimit"}
                                      control={control}
                                      render={({ field, fieldState }) => (
                                        <div className="flex items-center mt-5">
                                          <h3 className="text-sm text-gray-900 text-end">
                                            {"ポイント上限"}
                                            <span className="text-red-500 mx-1">*</span>
                                          </h3>
                                          <TextField
                                            {...field}
                                            type={"text"}
                                            label={""}
                                            color={"indigo"}
                                            className={"flex items-center gap-x-3 h-10"}
                                            selectClassName={"py-2 h-auto"}
                                            style={{ 'width': '5rem', 'text-align': 'right' }}
                                            inputClassName={`${(pointLimitFlg.value == 'true') ? "" : "bg-gray-300"}  mb-1`}
                                            disabled={!(pointLimitFlg.value == 'true')}
                                            maxLength={5}
                                          />
                                          {pointLimitFlg.value == 'true' && fieldState.error?.message && (
                                            <div className="block text-xs text-red-500" style={{ marginLeft: '5px' }}>{fieldState.error.message}</div>
                                          )}
                                        </div>
                                      )}
                                    />
                                    <div className="flex items-center mt-5 ml-7">
                                      <Controller
                                        name={"pointLimitMap.period"}
                                        control={control}
                                        render={({ field, fieldState }) => (
                                          <div className="flex items-center">
                                            <h3 className="text-sm text-gray-900 text-end">
                                              {"単位指定"}
                                              <span className="text-red-500 mx-1">*</span>
                                            </h3>
                                            <SelectFields
                                              {...field}
                                              color="indigo"
                                              className="h-10 flex items-center gap-x-3"
                                              label={""}
                                              selectClassName={`${(pointLimitFlg.value == 'true') ? "" : "bg-gray-300"} px-3 py-2 h-auto  mb-1`}
                                              selectItems={periodList}
                                              disabled={!(pointLimitFlg.value == 'true')}
                                              style={{ 'width': '10rem' }}
                                            />
                                            {pointLimitFlg.value == 'true' && fieldState.error?.message && (
                                              <div className="block text-xs text-red-500" style={{ marginLeft: '5px' }}>{fieldState.error.message}</div>
                                            )}
                                          </div>
                                        )}
                                      />
                                    </div>
                                  </div>
                                  <div className="ml-7 relative mt-5 shadow ring-1 ring-black ring-opacity-5 w-[32.5rem]" >
                                    <div className="flex">
                                      <span className={`absolute z-10 top-[-10px] left-[22px] text-sm text-gray-700 bg-white`}>
                                        <label className="mb-1 block text-sm font-medium text-gray-700">
                                          指定期間
                                        </label>
                                      </span>
                                    </div>
                                    <div className="flex mr-2 gap-x-3 ml-2">
                                      <div className="h-full pt-5 pb-5">
                                        <Controller
                                          name={"pointLimitMap.dateFrom"}
                                          control={control}
                                          render={({ field, fieldState }) => (
                                            <div className="flex flex-col gap-y-1">
                                              <div className="flex items-center">
                                                <h3 className="text-sm text-gray-900 text-end">
                                                  {"開始年月日"}
                                                  <span className="text-red-500 mx-1">*</span>
                                                </h3>
                                                <TextField
                                                  {...field}
                                                  type={"date"}
                                                  label={""}
                                                  color={"indigo"}
                                                  className={"flex items-center gap-x-3 h-10"}
                                                  selectClassName={"py-2 h-auto"}
                                                  style={{ 'width': '8.5rem' }}
                                                  disabled={(pointLimitFlg.value == 'true') ? (isPtDateEnabled ? false : true) : true}
                                                  inputClassName={`${(pointLimitFlg.value == 'true') ? (isPtDateEnabled ? "" : "bg-gray-300") : "bg-gray-300"}  mb-1`}
                                                />
                                              </div>
                                              {isPtDateEnabled && fieldState.error?.message && (
                                                <div className="block text-xs text-red-500" style={{ marginLeft: '98px' }}>{fieldState.error.message}</div>
                                              )}
                                            </div>
                                          )}
                                        />
                                      </div>
                                      <div className="h-full pt-5 pb-5">
                                        <Controller
                                          name={"pointLimitMap.dateTo"}
                                          control={control}
                                          render={({ field, fieldState }) => (
                                            <div className="flex flex-col gap-y-1">
                                              <div className="flex items-center">
                                                <h3 className="text-sm text-gray-900 text-end">
                                                  {"終了年月日"}
                                                  <span className="text-red-500 mx-1">*</span>
                                                </h3>
                                                <TextField
                                                  {...field}
                                                  type={"date"}
                                                  label={""}
                                                  color={"indigo"}
                                                  className={"flex items-center gap-x-3 h-10"}
                                                  selectClassName={"py-2 h-auto"}
                                                  style={{ 'width': '8.5rem' }}
                                                  disabled={(pointLimitFlg.value == 'true') ? (isPtDateEnabled ? false : true) : true}
                                                  inputClassName={`${(pointLimitFlg.value == 'true') ? (isPtDateEnabled ? "" : "bg-gray-300") : "bg-gray-300"}  mb-1`}
                                                />
                                              </div>
                                              {isPtDateEnabled && fieldState.error?.message && (
                                                <div className="block text-xs text-red-500 w-[8.5rem]" style={{ marginLeft: '98px' }}>{fieldState.error.message}</div>
                                              )}
                                            </div>
                                          )}
                                        />
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </>
                        )}
                        {getValues("typeId") == PointTypeId.VOLUNTEER_REGIST || getValues("typeId") == PointTypeId.GROUP_REGIST || getValues("typeId") == PointTypeId.FIRST_LOGIN ? (
                          <></>
                        ) : (
                          <>
                            <div className="relative mt-5 shadow ring-1 ring-black ring-opacity-5 w-[54rem]">
                              <div className="flex">
                                <span className={`absolute z-10 top-[-10px] left-[22px] text-sm text-gray-700 bg-white`}>
                                  <label className="mb-1 block text-sm font-medium text-gray-700">
                                    ボーナスポイント<span className="text-red-500 mx-1">*</span>
                                  </label>
                                </span>
                              </div>
                              <div className="pl-5 pt-2 pb-5">
                                <Controller
                                  name={"bonusSettingFlg"}
                                  control={control}
                                  render={({ field, fieldState }) => (
                                    <div className="flex flex-col gap-y-1">
                                      <RadioButton
                                        children={undefined}
                                        name={"bonusSettingFlg"}
                                        value={bonusSettingFlg}
                                        radioButtonItem={bonusSettingSelectFlgItems}
                                        disabled={
                                          (getValues("typeId") == PointTypeId.STEPS_RANGE || getValues("typeId") == PointTypeId.STEPS_SPECIFIED || getValues("typeId") == PointTypeId.STEPS_TARGET)}
                                      />
                                      {fieldState.error?.message && (
                                        <div className="block text-xs text-red-500">{fieldState.error.message}</div>
                                      )}
                                    </div>
                                  )}
                                />
                                <div className="h-full mt-4">
                                  <div className={"px-6"}>
                                    <TableList
                                      header={false}
                                      footer={false}
                                      defaultSortKey={"sort"}
                                      sort={false}
                                      gridCol={bonusPointTableColumns}
                                      gridRow={bonusSettingFlg.value == 'true' ? bonusPointTableRowList : []}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                            <BonusPointSettingsModal
                              ref={pointModalRef}
                              open={isBonusPointSettingsModalOpen}
                              toggle={closeBonusPointSettingsModal}
                              closeOnClickOutside
                              onSubmit={handleBonusPointSettingsSubmit}
                              children={undefined}
                              bonusPointSettings={bonusPointData}
                            />
                          </>
                        )}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <ModalFoterBox>
              <Button
                className="mx-2 mt-1 w-[132px]"
                outline
                onClick={toggle}
                children={"キャンセル"}
              />
              <Button
                className="mt-1 w-[132px]"
                onClick={handleSubmit(onClickSubmit)}
                children={"登録"}
              />
            </ModalFoterBox>
          </Dialog.Panel>
        </div>
      </Modal>
    </>
  );
};